import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

// MUI Components
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import CardActions from '@mui/material/CardActions';
import CardMedia from '@mui/material/CardMedia';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import LinearProgress from '@mui/material/LinearProgress';
import Alert from '@mui/material/Alert';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemIcon from '@mui/material/ListItemIcon';
import Paper from '@mui/material/Paper';
import Tooltip from '@mui/material/Tooltip';
import Avatar from '@mui/material/Avatar';
import TextField from '@mui/material/TextField';
import Snackbar from '@mui/material/Snackbar';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';

// MUI Icons - Individual imports
import ThumbUpAltOutlinedIcon from '@mui/icons-material/ThumbUpAltOutlined';
import ThumbDownAltOutlinedIcon from '@mui/icons-material/ThumbDownAltOutlined';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningIcon from '@mui/icons-material/Warning';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import CategoryIcon from '@mui/icons-material/Category';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PaymentIcon from '@mui/icons-material/Payment';
import SubtasksIcon from '@mui/icons-material/ListAlt';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DescriptionIcon from '@mui/icons-material/Description';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ImageIcon from '@mui/icons-material/Image';
import EventIcon from '@mui/icons-material/Event';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PersonIcon from '@mui/icons-material/Person';
import DownloadIcon from '@mui/icons-material/Download';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import LabelIcon from '@mui/icons-material/Label';
import LockIcon from '@mui/icons-material/Lock';
import EnhancedEncryptionIcon from '@mui/icons-material/EnhancedEncryption';
import ErrorIcon from '@mui/icons-material/Error';
import DateRangeIcon from '@mui/icons-material/DateRange';
import EmailIcon from '@mui/icons-material/Email';
import NotificationsIcon from '@mui/icons-material/Notifications';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import SendIcon from '@mui/icons-material/Send';

// App imports
import { 
  getListTasks, 
  getTaskAttachments, 
  authorizeTask, 
  getTaskSubtasks,
  confirmReceptionTask,
  updateTaskPriority,
  approveRequest,
  denyRequest,
  getTaskComments,
  postTaskComment
} from '../services/clickupService';
import { useAuth } from '../context/AuthContext';
import ErrorBoundary from '../components/ErrorBoundary';
import { classifyTaskStatus } from '../utils/taskStatus';

/**
 * Página para mostrar las tareas de una categoría específica dentro de una lista
 */
const CategoryTasksPage = () => {
  const { listId, listName, status } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const { user, exchangeRate, exchangeRateDate } = useAuth();
  const includeCompletedFromUrl = searchParams.get('includeCompleted') === 'true';
  
  // Estados
  const [tasks, setTasks] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [, setListData] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  // Estado para capturar errores de renderizado
  const [, setRenderError] = useState(null);
  // Estado para manejar la carga de adjuntos
  const [taskAttachments, setTaskAttachments] = useState([]);
  const [loadingAttachments, setLoadingAttachments] = useState(false);
  const [attachmentError, setAttachmentError] = useState('');
  const [taskComments, setTaskComments] = useState([]);
  const [loadingComments, setLoadingComments] = useState(false);
  const [commentsError, setCommentsError] = useState('');
  const [viewCommentsUrl, setViewCommentsUrl] = useState('');
  const [newCommentText, setNewCommentText] = useState('');
  const [isPostingComment, setIsPostingComment] = useState(false);
  const [postCommentError, setPostCommentError] = useState('');
  // Estado para manejar la depuración de campos
  const [debugInfo, setDebugInfo] = useState(null);
  const [showDebugInfo, setShowDebugInfo] = useState(false);
  const [isUpdatingPriority, setIsUpdatingPriority] = useState(false);
  const [priorityUpdateError, setPriorityUpdateError] = useState(null);
  const [isAuthorizing, setIsAuthorizing] = useState(false);
  const [, setAuthorizeError] = useState(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  // Agregar un nuevo estado para el diálogo de requisitos
  const [showRequirementsDialog, setShowRequirementsDialog] = useState(false);
  // Añadir nuevo estado para el diálogo de resultado de Kepler
  const [keplerResultDialog, setKeplerResultDialog] = useState(false);
  const [keplerResult, setKeplerResult] = useState(null);
  // Estados para subtareas
  const [taskSubtasks, setTaskSubtasks] = useState([]);
  const [loadingSubtasks, setLoadingSubtasks] = useState(false);
  const [subtasksError, setSubtasksError] = useState('');
  const [openSubtasksDialog, setOpenSubtasksDialog] = useState(false);
  const [confirmReceptionDialogOpen, setConfirmReceptionDialogOpen] = useState(false);
  const [isConfirmingReception, setIsConfirmingReception] = useState(false);
  const [, setConfirmReceptionError] = useState(null);
  // Estado para expandir/colapsar la descripción de la tarea en el modal de detalles
  const [descriptionExpanded, setDescriptionExpanded] = useState(false);
  // Estados para flujo Aprobar / Denegar (estado Solicitado)
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [denyDialogOpen, setDenyDialogOpen] = useState(false);
  const [denyReason, setDenyReason] = useState('');
  const [isProcessingApproval, setIsProcessingApproval] = useState(false);
  const [approvalError, setApprovalError] = useState(null);
  const [approvalSnackbar, setApprovalSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [showCompletedTasks, setShowCompletedTasks] = useState(includeCompletedFromUrl);
  
  // ---- BEGIN READ-ONLY CHECK ----
  // Determine if the current user is read-only
  const isReadOnlyUser = user?.isReadOnly === true;
  // ---- END READ-ONLY CHECK ----
  
  // Decodificamos los parámetros
  const decodedListName = decodeURIComponent(listName);
  const decodedStatus = decodeURIComponent(status);

  useEffect(() => {
    setShowCompletedTasks(includeCompletedFromUrl);
  }, [includeCompletedFromUrl]);
  
  // Manejador de errores global
  useEffect(() => {
    // Función para manejar errores no capturados
    const handleGlobalError = (event) => {
      console.error('Error no capturado:', event.error);
      setRenderError(event.error?.message || 'Ha ocurrido un error inesperado');
      // Prevenir que el error rompa toda la aplicación
      event.preventDefault();
    };
    
    // Agregar listener para errores
    window.addEventListener('error', handleGlobalError);
    
    // Limpiar al desmontar
    return () => {
      window.removeEventListener('error', handleGlobalError);
    };
  }, []);
  
  // Función para depurar campos personalizados
  const logCustomFieldDebugInfo = (task) => {
    if (process.env.NODE_ENV !== 'production') {
      console.group('Depuración de campos personalizados de tarea:', task.id);
      if (task.tipoOrden) {
        console.log('Tipo de Orden:', {
          valor: task.tipoOrden.valor,
          descripcion: task.tipoOrden.descripcion,
          formato: task.tipoOrden.formato,
          id: task.tipoOrden.campo_id
        });
      }
      if (task.modulo) {
        console.log('Módulo:', {
          valor: task.modulo.valor,
          descripcion: task.modulo.descripcion,
          formato: task.modulo.formato,
          id: task.modulo.campo_id
        });
      }
      if (task.formaPago) {
        console.log('Forma de Pago:', {
          valor: task.formaPago.valor,
          descripcion: task.formaPago.descripcion,
          formato: task.formaPago.formato,
          id: task.formaPago.campo_id
        });
      }
      if (task.avance) {
        console.log('Avance:', {
          valor: task.avance.valor,
          formato: task.avance.formato,
          id: task.avance.campo_id
        });
      }
      console.groupEnd();
    }
  };

  // Determina el tipo de cambio a usar: toma el valor de la tarea si es válido (>0); 
  // de lo contrario usa el TC de sesión si existe y está vigente según la fecha C32.
  const determineExchangeRate = (taskExchangeRate) => {
    let finalRate = null;
    const taskRate = taskExchangeRate && parseFloat(taskExchangeRate) > 0 ? parseFloat(taskExchangeRate) : null;
    const sessionRate = exchangeRate && parseFloat(exchangeRate) > 0 ? parseFloat(exchangeRate) : null;
    const sessionRateDateStr = exchangeRateDate;

    if (taskRate) {
      finalRate = taskRate;
    } else if (sessionRate && sessionRateDateStr) {
      try {
        const [y, m, d] = sessionRateDateStr.split('-').map(Number);
        if (y && m && d) {
          const today = new Date();
          today.setHours(0,0,0,0);
          const expiry = new Date(y, m - 1, d);
          expiry.setHours(0,0,0,0);
          if (today <= expiry) {
            finalRate = sessionRate;
          }
        }
      } catch(err) {
        console.warn('No se pudo validar fecha de TC de sesión:', err);
      }
    }
    return finalRate;
  };

  // Función para obtener y procesar adjuntos de una tarea específica
  const fetchTaskAttachments = async (taskId) => {
    if (!taskId) return;
    setLoadingAttachments(true);
    setAttachmentError('');
    try {
      const response = await getTaskAttachments(taskId);
      setTaskAttachments(response.attachments || []);

      // Si existe una URL directa para ver los adjuntos en ClickUp, guardarla en la tarea seleccionada
      if (response.viewAttachmentsUrl) {
        setSelectedTask(prevTask => ({
          ...prevTask,
          viewAttachmentsUrl: response.viewAttachmentsUrl
        }));
      }
    } catch (error) {
      console.error('Error cargando adjuntos de tarea:', error);
      let errorMessage = 'No se pudieron cargar los adjuntos.';

      if (error.response) {
        if (error.response.status === 429) {
          errorMessage = 'No se pudieron cargar los adjuntos debido a límites de la API. Intente más tarde o vea los adjuntos directamente en ClickUp.';
        } else if (error.response.data && error.response.data.message) {
          errorMessage = error.response.data.message;
        }

        // Si la respuesta contiene una URL para ver adjuntos, usarla
        if (error.response.data && error.response.data.viewAttachmentsUrl) {
          setSelectedTask(prevTask => ({
            ...prevTask,
            viewAttachmentsUrl: error.response.data.viewAttachmentsUrl
          }));
        }
      }

      setAttachmentError(errorMessage);
    } finally {
      setLoadingAttachments(false);
    }
  };

  const fetchTaskComments = async (taskId) => {
    if (!taskId) return;
    setLoadingComments(true);
    setCommentsError('');
    setPostCommentError('');
    try {
      const response = await getTaskComments(taskId);
      setTaskComments(response.comments || []);
      if (response.viewCommentsUrl) {
        setViewCommentsUrl(response.viewCommentsUrl);
      }
    } catch (error) {
      console.error('Error cargando comentarios de tarea:', error);
      let errorMessage = 'No se pudieron cargar los comentarios.';

      if (error.response) {
        if (error.response.status === 429) {
          errorMessage = 'No se pudieron cargar los comentarios debido a límites de la API. Intente más tarde o vea la actividad directamente en ClickUp.';
        } else if (error.response.data?.message) {
          errorMessage = error.response.data.message;
        }

        if (error.response.data?.viewCommentsUrl) {
          setViewCommentsUrl(error.response.data.viewCommentsUrl);
        }
      }

      setCommentsError(errorMessage);
    } finally {
      setLoadingComments(false);
    }
  };

  // Los comentarios están permitidos incluso para cuentas de solo lectura
  const handlePostComment = async () => {
    if (!selectedTask?.id || !newCommentText.trim()) return;

    setIsPostingComment(true);
    setPostCommentError('');

    try {
      const response = await postTaskComment(selectedTask.id, newCommentText.trim());
      setNewCommentText('');
      if (response.comment) {
        setTaskComments((prevComments) => [response.comment, ...prevComments]);
      } else {
        await fetchTaskComments(selectedTask.id);
      }
      if (response.viewCommentsUrl) {
        setViewCommentsUrl(response.viewCommentsUrl);
      }
    } catch (error) {
      console.error('Error publicando comentario:', error);
      let errorMessage = 'No se pudo publicar el comentario.';

      if (error.response) {
        if (error.response.status === 429) {
          errorMessage = 'No se pudo publicar el comentario debido a límites de la API. Intente más tarde o escríbalo directamente en ClickUp.';
        } else if (error.response.data?.message) {
          errorMessage = error.response.data.message;
        }

        if (error.response.data?.viewCommentsUrl) {
          setViewCommentsUrl(error.response.data.viewCommentsUrl);
        }
      }

      setPostCommentError(errorMessage);
    } finally {
      setIsPostingComment(false);
    }
  };

  const getCommentAuthorLabel = (comment) => {
    if (comment?.keplerAuthor?.name) {
      return comment.keplerAuthor.name;
    }
    return comment?.clickUpUser?.username || 'Usuario';
  };

  const getCommentAuthorInitial = (comment) => {
    const label = getCommentAuthorLabel(comment);
    return label.charAt(0).toUpperCase();
  };

  const handleOpenDialog = async (task) => {
    try {
      setAttachmentError('');
      setSubtasksError('');
      setCommentsError('');
      setPostCommentError('');
      setNewCommentText('');
      setViewCommentsUrl('');

      // Calcular tipo de cambio que se mostrará (paridad c84)
      const finalTC = determineExchangeRate(task?.tipoCambio?.valor);
      const enrichedTask = {
        ...task,
        tipoCambioFinal: finalTC !== null ? finalTC : task?.tipoCambio?.valor,
      };

      setSelectedTask(enrichedTask);
      setOpenDialog(true);
      
      // Cargar adjuntos solo cuando se abre la tarea
      await fetchTaskAttachments(task.id);
      await fetchTaskComments(task.id);
      
      // Cargar subtareas para mostrar indicador de cantidad
      try {
        const subtasks = await fetchTaskSubtasks(task.id);
        // Actualizar la tarea seleccionada con el conteo de subtareas
        setSelectedTask(prevTask => ({
          ...prevTask,
          subtasksCount: subtasks.length
        }));
      } catch (subtaskErr) {
        console.error('Error al cargar subtareas para indicador:', subtaskErr);
        // No mostramos error aquí, solo fallamos silenciosamente para el indicador
      }
      
      logCustomFieldDebugInfo(task); // Para depuración
    } catch (err) {
      console.error('Error al abrir diálogo:', err);
      setRenderError('Ocurrió un error al mostrar los detalles de la tarea.');
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setDescriptionExpanded(false);
    // Limpiar la tarea seleccionada y los adjuntos después de cerrar
    setTimeout(() => {
      setSelectedTask(null);
      setTaskAttachments([]);
      setAttachmentError('');
      setTaskComments([]);
      setCommentsError('');
      setPostCommentError('');
      setNewCommentText('');
      setViewCommentsUrl('');
      setDebugInfo(null);
      setShowDebugInfo(false);
    }, 300); // Pequeño retraso para evitar parpadeo visual
  };
  
  // Cargar datos directamente desde ClickUp
  useEffect(() => {
    const loadListTasks = async () => {
      setIsLoading(true);
      setError('');
      
      try {
        // Usamos el nuevo endpoint para obtener tareas específicas filtradas por status
        console.log(`Obteniendo tareas para lista ${listId} con status ${decodedStatus}, includeCompleted: ${showCompletedTasks}`);
        const response = await getListTasks(listId, decodedStatus, { includeCompleted: showCompletedTasks });
        
        setListData({
          listId: response.listId,
          listName: response.listName,
          totalTasks: response.totalTasks
        });
        
        setTasks(response.tasks);
        console.log(`Tareas cargadas: ${response.tasks.length}`);
      } catch (error) {
        console.error('Error cargando datos de la lista:', error);
        let errorMessage = 'Ocurrió un error al cargar las tareas.';
        
        if (error.response) {
          if (error.response.status === 401) {
            errorMessage = 'Sesión expirada. Por favor, vuelva a iniciar sesión.';
          } else if (error.response.status === 404) {
            errorMessage = 'No se encontró la lista seleccionada en ClickUp.';
          } else if (error.response.data && error.response.data.message) {
            errorMessage = `Error: ${error.response.data.message}`;
          }
        } else if (error.request) {
          errorMessage = 'No se pudo conectar con el servidor. Verifique su conexión a internet.';
        }
        
        setError(errorMessage);
      } finally {
        setIsLoading(false);
      }
    };
    
    loadListTasks();
  }, [listId, decodedStatus, showCompletedTasks]);
  
  // Función para formatear fecha
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    try {
      // ClickUp uses Unix timestamp in milliseconds
      const date = typeof dateString === 'number' 
        ? new Date(dateString) // It's already a number (timestamp)
        : /^\d+$/.test(dateString) 
          ? new Date(parseInt(dateString)) // It's a string containing only digits
          : new Date(dateString); // It's a standard date string
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        console.warn('Invalid date value:', dateString);
        return 'N/A';
      }
      
      return date.toLocaleDateString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch (err) {
      console.error('Error formatting date:', err, dateString);
      return 'N/A';
    }
  };
  
  // Función para formatear fecha y hora
  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    
    try {
      // ClickUp uses Unix timestamp in milliseconds
      const date = typeof dateString === 'number' 
        ? new Date(dateString) // It's already a number (timestamp)
        : /^\d+$/.test(dateString) 
          ? new Date(parseInt(dateString)) // It's a string containing only digits
          : new Date(dateString); // It's a standard date string
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        console.warn('Invalid date value:', dateString);
        return 'N/A';
      }
      
      return date.toLocaleDateString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (err) {
      console.error('Error formatting datetime:', err, dateString);
      return 'N/A';
    }
  };

  // Función para formatear el tamaño de archivo
  const formatFileSize = (bytes) => {
    if (!bytes) return 'N/A';
    
    const units = ['B', 'KB', 'MB', 'GB'];
    let size = bytes;
    let unitIndex = 0;
    
    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex++;
    }
    
    return `${size.toFixed(1)} ${units[unitIndex]}`;
  };

  // Función para obtener el ícono adecuado según el tipo de archivo
  const getFileIcon = (fileType) => {
    if (!fileType) return <InsertDriveFileIcon />;
    
    const type = fileType.toLowerCase();
    
    if (type === 'pdf') {
      return <PictureAsPdfIcon color="error" />;
    } else if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(type)) {
      return <ImageIcon color="primary" />;
    } else {
      return <InsertDriveFileIcon color="action" />;
    }
  };
  
  // Mapea cada clave de status a un color semantico de MUI (para Chips, etc.)
  const STATUS_TO_MUI_COLOR = {
    forApproval: 'secondary',
    received: 'info',
    warranty: 'info',
    inProgress: 'info',
    done: 'success',
    review: 'secondary',
    blocked: 'error',
    canceled: 'error',
    pending: 'warning',
    open: 'primary',
    requested: 'warning',
    approved: 'success',
    denied: 'error',
  };

  const getStatusColor = (taskStatus, statusType) => {
    if (!taskStatus) return 'primary';
    return STATUS_TO_MUI_COLOR[classifyTaskStatus(taskStatus, statusType)] || 'primary';
  };

  const getStatusBackgroundColor = (status, statusType) => {
    const palette = theme.palette.statuses || {};
    const key = classifyTaskStatus(status, statusType);
    return palette[key]?.bg || theme.palette.grey[100];
  };
  
  // Función para formatear el porcentaje
  const formatPercentage = (value) => {
    if (typeof value !== 'number') {
      return 'N/A';
    }
    return `${Math.round(value)}%`;
  };

  // Componente para mostrar la barra de progreso con etiqueta
  function LinearProgressWithLabel(props) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
        <Box sx={{ width: '100%', mr: 1 }}>
          <LinearProgress variant="determinate" {...props} />
        </Box>
        <Box sx={{ minWidth: 35 }}>
          <Typography variant="body2" color="text.secondary">
            {formatPercentage(props.value)}
          </Typography>
        </Box>
      </Box>
    );
  }
  
  // Función para verificar si una tarea puede ser autorizada
  const canAuthorizeTask = (task) => {
    if (!task) return { canAuthorize: false, reason: 'No hay tarea seleccionada' };
  
    // Verificar que la tarea esté en estado "por autorizar"
    if (!task.status || !task.status.toLowerCase().includes('por autorizar')) {
      return { 
        canAuthorize: false, 
        reason: 'La tarea no está en estado de autorización' 
      };
    }
    
    // Verificar que tenga monto de orden
    if (!task.montoOrden || (typeof task.montoOrden.valor === 'number' ? task.montoOrden.valor === 0 : !task.montoOrden.valor)) {
      return {
        canAuthorize: false,
        reason: 'Falta el monto de la orden'
      };
    }
    
    // Verificar que tenga tipo de orden
    if (!task.tipoOrden || task.tipoOrden.valor === undefined || task.tipoOrden.valor === null || task.tipoOrden.valor === '') {
      return {
        canAuthorize: false,
        reason: 'Falta el tipo de orden'
      };
    }
    
    // Verificar que tenga forma de pago
    if (!task.formaPago || task.formaPago.valor === undefined || task.formaPago.valor === null || task.formaPago.valor === '') {
      return {
        canAuthorize: false,
        reason: 'Falta la forma de pago'
      };
    }
    
    // Verificar que tenga módulo
    if (!task.modulo || task.modulo.valor === undefined || task.modulo.valor === null || task.modulo.valor === '') {
      return {
        canAuthorize: false,
        reason: 'Falta el módulo'
      };
    }
    
    // Verificar que tenga al menos un adjunto
    if (!taskAttachments || taskAttachments.length === 0) {
      return {
        canAuthorize: false,
        reason: 'La tarea debe tener al menos un documento adjunto'
      };
    }
    
    return { canAuthorize: true };
  };

  // Renderizar tarjeta de tarea
  const renderTaskCard = (task) => {
    let cardBorderColor = theme.palette.divider;
    let validStatusColor = 'default';

    if (task.status) {
      const key = classifyTaskStatus(task.status, task.statusType);
      const statusEntry = theme.palette.statuses?.[key];
      if (statusEntry?.main) {
        cardBorderColor = statusEntry.main;
      }
      validStatusColor = STATUS_TO_MUI_COLOR[key] || 'default';
    }
    
    // Estilo especial para "confirma recepcion"
    const isConfirmacionRecepcion = task.status && task.status.toLowerCase().includes('confirma recepcion');
    
    return (
      <Card 
        key={task.id} 
        sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          border: '1px solid',
          borderColor: cardBorderColor,
          borderWidth: isConfirmacionRecepcion ? '2px' : '1px',
          cursor: 'pointer',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: 4,
            borderColor: cardBorderColor
          },
          boxShadow: isConfirmacionRecepcion ? 4 : 1,
          position: 'relative',
          overflow: 'visible'
        }}
        onClick={() => handleOpenDialog(task)}
      >
        {isConfirmacionRecepcion && (
          <Box
            sx={{
              position: 'absolute',
              top: '-8px',
              right: '-8px',
              width: '20px',
              height: '20px',
              borderRadius: '50%',
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: 2,
              zIndex: 1
            }}
          >
            <CheckCircleIcon fontSize="small" />
          </Box>
        )}
        <CardHeader
          title={
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>
              {task.name}
              <Typography 
                component="span" 
                variant="caption" 
                color="text.secondary" 
                sx={{ ml: 1 }}
              >
                (ID: {task.id})
              </Typography>
            </Typography>
          }
          sx={{ pb: 1 }}
        />
        <Divider />
        <CardContent sx={{ pt: 2, pb: 1, flexGrow: 1 }}>
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                {task.status && task.status.toLowerCase().includes('confirma recepcion') ? (
                  <Chip 
                    label={task.status} 
                    color="primary"
                    size="small"
                    sx={{ 
                      fontWeight: 'bold',
                      animation: isConfirmacionRecepcion ? 'pulse 1.5s infinite' : 'none',
                      '@keyframes pulse': {
                        '0%': {
                          boxShadow: '0 0 0 0 rgba(25, 118, 210, 0.7)',
                        },
                        '70%': {
                          boxShadow: '0 0 0 10px rgba(25, 118, 210, 0)',
                        },
                        '100%': {
                          boxShadow: '0 0 0 0 rgba(25, 118, 210, 0)',
                        },
                      },
                    }}
                  />
                ) : (
                  <Chip 
                    label={task.status} 
                    color={validStatusColor}
                    size="small"
                    sx={{ fontWeight: 'medium' }}
                  />
                )}
              </Box>
            </Grid>
            
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <EventIcon fontSize="small" color="action" sx={{ mr: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  Creado: {formatDate(task.dateCreated)}
                </Typography>
              </Box>
            </Grid>
            
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <CalendarTodayIcon fontSize="small" color="action" sx={{ mr: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  Fecha límite: {formatDate(task.dueDate)}
                </Typography>
              </Box>
            </Grid>
            
            {task.montoOrden && (typeof task.montoOrden.valor === 'number' ? task.montoOrden.valor !== 0 : task.montoOrden.valor) && (
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <MonetizationOnIcon fontSize="small" color="success" sx={{ mr: 1 }} />
                  <Typography variant="body2" fontWeight="bold" color="success.main">
                    {task.montoOrden.nombre}: ${typeof task.montoOrden.valor === 'number' 
                      ? task.montoOrden.valor.toLocaleString('es-MX')
                      : task.montoOrden.valor
                    }
                  </Typography>
                </Box>
              </Grid>
            )}
            
            {task.tipoCambio && (typeof task.tipoCambio.valor === 'number' ? task.tipoCambio.valor !== 0 : task.tipoCambio.valor) && (
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <MonetizationOnIcon fontSize="small" color="info" sx={{ mr: 1 }} />
                  <Typography variant="body2" fontWeight="bold" color="info.main">
                    {task.tipoCambio.nombre}: {typeof task.tipoCambio.valor === 'number' 
                      ? task.tipoCambio.valor.toLocaleString('es-MX')
                      : task.tipoCambio.valor
                    }
                  </Typography>
                </Box>
              </Grid>
            )}
            

            
            {task.montoConsumido && (
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <MonetizationOnIcon fontSize="small" color="warning" sx={{ mr: 1 }} />
                  <Typography variant="body2" fontWeight="bold" color="warning.main">
                    {task.montoConsumido.nombre}: ${typeof task.montoConsumido.valor === 'number' 
                      ? task.montoConsumido.valor.toLocaleString('es-MX')
                      : parseFloat(task.montoConsumido.valor).toLocaleString('es-MX')
                    }
                  </Typography>
                </Box>
              </Grid>
            )}
            

            

            
            {task.tipoOrden && (
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <CategoryIcon fontSize="small" color="primary" sx={{ mr: 1 }} />
                  <Typography variant="body2" color="text.primary">
                    {task.tipoOrden.nombre}: {task.tipoOrden.descripcion || task.tipoOrden.valor || 'N/A'}
                    {!task.tipoOrden.descripcion && task.tipoOrden.valor !== null && (
                      <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                        (Sin mapeo para valor: {task.tipoOrden.valor})
                      </Typography>
                    )}
                  </Typography>
                </Box>
              </Grid>
            )}
            
            {task.formaPago && (
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <PaymentIcon fontSize="small" color="info" sx={{ mr: 1 }} />
                  <Typography variant="body2" color="text.primary">
                    {task.formaPago.nombre}: {task.formaPago.descripcion || task.formaPago.valor || 'N/A'}
                  </Typography>
                </Box>
              </Grid>
            )}
            
            {task.modulo && (
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <ViewModuleIcon fontSize="small" color="secondary" sx={{ mr: 1 }} />
                  <Typography variant="body2" color="text.primary">
                    {task.modulo.nombre}: {task.modulo.descripcion || task.modulo.valor || 'N/A'}
                    {!task.modulo.descripcion && task.modulo.valor !== null && (
                      <Typography variant="caption" color="text.secondary" sx={{ ml: 1, display: 'block' }}>
                        (ID: {selectedTask.modulo.campo_id}, Valor: {selectedTask.modulo.valor})
                      </Typography>
                    )}
                  </Typography>
                </Box>
              </Grid>
            )}

            {task.avance && (
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <TrendingUpIcon fontSize="small" color="action" sx={{ mr: 1 }} />
                  <Typography variant="body2" color="text.primary" sx={{ mr: 1 }}>
                    {task.avance.nombre}:
                  </Typography>
                  <Box sx={{ width: '50%' }}> 
                    <LinearProgressWithLabel value={task.avance.valor || 0} />
                  </Box>
                </Box>
              </Grid>
            )}

            {task.assignees && task.assignees.length > 0 && (
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <PersonIcon fontSize="small" color="action" sx={{ mr: 1 }} />
                  <Typography variant="body2" color="text.secondary" noWrap>
                    {task.assignees.join(', ')}
                  </Typography>
                </Box>
              </Grid>
            )}

            {task.attachments && task.attachments.length > 0 && (
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <AttachFileIcon fontSize="small" color="primary" sx={{ mr: 1 }} />
                  <Typography variant="body2" color="primary" noWrap>
                    {task.attachments.length} {task.attachments.length === 1 ? 'adjunto' : 'adjuntos'}
                  </Typography>
                </Box>
              </Grid>
            )}

            {/* Mostrar indicador cuando hay adjuntos pero no se pudieron cargar debido a límites de tasa */}
            {!task.attachments?.length && task.hasAttachments && (
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <AttachFileIcon fontSize="small" color="action" sx={{ mr: 1 }} />
                  <Typography variant="body2" color="text.secondary" noWrap>
                    Tiene adjuntos
                  </Typography>
                </Box>
              </Grid>
            )}

            {task.priority && (
              <PriorityChip priority={task.priority} />
            )}

            {/* Indicador de si la tarea puede ser autorizada o no */}
            {task.status && task.status.toLowerCase().includes('por autorizar') && (
              <Box sx={{ mt: 2, display: 'flex', alignItems: 'center' }}>
                <Tooltip 
                  title="Abra para verificar si esta tarea puede ser autorizada. Se requiere monto, tipo de orden, forma de pago, módulo y al menos un documento adjunto."
                  arrow
                  placement="top"
                  PopperProps={{
                    modifiers: [
                      {
                        name: 'offset',
                        options: {
                          offset: [0, -5],
                        },
                      },
                    ],
                  }}
                >
                  <Chip
                    icon={<WarningIcon />}
                    label="Abrir para verificar requisitos"
                    size="small"
                    color="warning"
                    sx={{ 
                      mr: 1, 
                      bgcolor: 'warning.main', 
                      color: 'warning.contrastText',
                      fontWeight: 'bold',
                      border: '1px solid',
                      borderColor: 'warning.dark'
                    }}
                  />
                </Tooltip>
              </Box>
            )}
          </Grid>
        </CardContent>
      </Card>
    );
  };
  
  // Renderizar tarjetas de documentos adjuntos
  const renderAttachmentCards = (task) => {
    // Usar los adjuntos que cargamos específicamente para esta tarea
    const attachments = taskAttachments;
    
    const probablyHasAttachments = task?.hasAttachments || false;
    
    // Si hay error al cargar adjuntos, mostrarlo
    if (attachmentError) {
      return (
        <Box>
          <Alert severity="warning" sx={{ mb: 2 }}>
            {attachmentError}
          </Alert>
        </Box>
      );
    }
    
    // Si estamos cargando los adjuntos, mostrar indicador
    if (loadingAttachments) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', my: 3 }}>
          <CircularProgress size={30} sx={{ mb: 2 }} />
          <Typography variant="body2">Cargando documentos adjuntos...</Typography>
        </Box>
      );
    }
    
    if (!attachments || attachments.length === 0) {
      return (
        <Box>
          <Typography variant="body2" color="text.secondary" paragraph>
            {probablyHasAttachments ? 
              "No se encontraron documentos adjuntos para esta tarea." : 
              "No hay documentos adjuntos para esta tarea."}
          </Typography>
        </Box>
      );
    }
    
    // Si hay adjuntos, mostrarlos junto con el enlace directo
    return (
      <Box>
        <Grid container spacing={2}>
          {/* Mostrar los adjuntos que sí se pudieron cargar */}
          {processAttachments(attachments).map((attachment) => (
            <Grid item xs={12} sm={6} md={4} key={attachment.id}>
              <Card 
                elevation={1}
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-3px)',
                    boxShadow: 3
                  }
                }}
              >
                {attachment.thumbnail ? (
                  <CardMedia
                    component="img"
                    height="140"
                    image={attachment.thumbnail}
                    alt={attachment.title}
                    sx={{ objectFit: 'contain', bgcolor: '#f5f5f5' }}
                  />
                ) : (
                  <Box 
                    sx={{ 
                      height: 140, 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      bgcolor: '#f5f5f5',
                      flexDirection: 'column',
                      p: 2
                    }}
                  >
                    {getFileIcon(attachment.type)}
                    <Typography 
                      variant="caption" 
                      color="text.secondary" 
                      align="center"
                      sx={{ mt: 1 }}
                    >
                      {attachment.type ? attachment.type.toUpperCase() : 'ARCHIVO'}
                    </Typography>
                  </Box>
                )}
                <CardContent sx={{ py: 1, flexGrow: 1 }}>
                  <Tooltip title={attachment.title}>
                    <Typography 
                      variant="subtitle2" 
                      noWrap 
                      sx={{ fontWeight: 'medium' }}
                    >
                      {attachment.title}
                    </Typography>
                  </Tooltip>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      {attachment.size ? formatFileSize(attachment.size) : 'Tamaño desconocido'}
                    </Typography>
                    {attachment.date && (
                      <Typography variant="caption" color="text.secondary">
                        {formatDate(attachment.date)}
                      </Typography>
                    )}
                  </Box>
                </CardContent>
                <CardActions>
                  <Button 
                    size="small" 
                    color="primary" 
                    startIcon={<DownloadIcon />}
                    fullWidth
                    href={attachment.url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Descargar
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  };
  
  // Función auxiliar para procesar adjuntos
  const processAttachments = (attachments) => {
    if (!attachments || !Array.isArray(attachments)) return [];
    
    return attachments.map(attachment => {
      // Detectar tipo de archivo por extensión o URL
      let fileType = attachment.type || attachment.extension || 'unknown';
      if (fileType === 'unknown' && attachment.url) {
        const urlParts = attachment.url.split('.');
        if (urlParts.length > 1) {
          fileType = urlParts[urlParts.length - 1].toLowerCase();
        }
      }
      
      // Si la URL contiene 'clickup.com' y 'thumbnail', podemos intentar generar una URL de miniatura
      let thumbnail = attachment.thumbnail || null;
      
      return {
        ...attachment,
        id: attachment.id || `att-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        title: attachment.title || 'Documento adjunto',
        type: fileType,
        thumbnail: thumbnail
      };
    });
  };
  
  // Función para actualizar la prioridad de una tarea
  const handlePriorityChange = async (event) => {
    const newPriority = parseInt(event.target.value, 10); // Asegurar que sea número
    setPriorityUpdateError(null);
    setIsUpdatingPriority(true);
    
    try {
      const response = await updateTaskPriority(selectedTask.id, newPriority);
      console.log('Respuesta de actualización de prioridad:', response);
      
      // Extraer la prioridad de la respuesta
      const { priority, priorityLabel, priorityRaw } = response.task;
      
      // Actualizar la tarea seleccionada con la nueva prioridad
      const updatedTask = {
        ...selectedTask,
        priority: priorityRaw || priority, // Usar el objeto completo si está disponible
        priorityLabel: priorityLabel
      };
      
      setSelectedTask(updatedTask);
      
      // Actualizar también la tarea en la lista
      setTasks(tasks.map(task => 
        task.id === selectedTask.id 
          ? {...task, priority: priorityRaw || priority}
          : task
      ));
      
      console.log('Prioridad actualizada correctamente:', newPriority, priorityLabel);
      
    } catch (error) {
      console.error('Error al actualizar la prioridad:', error);
      setPriorityUpdateError(
        error.response?.data?.message || 
        'Error al actualizar la prioridad. Intente nuevamente.'
      );
    } finally {
      setIsUpdatingPriority(false);
    }
  };
  
  // Función para obtener la etiqueta de prioridad
  const getPriorityLabel = (priority) => {
    // Log para depuración
    console.log('getPriorityLabel recibió:', priority);
    
    // Si es un número, usar directamente
    if (typeof priority === 'number') {
      switch (priority) {
        case 1: return 'Urgente';
        case 2: return 'Alta';
        case 3: return 'Normal';
        case 4: return 'Baja';
        default: return 'Normal';
      }
    }
    
    // Si es un objeto, intentar extraer el valor adecuado
    if (priority && typeof priority === 'object') {
      // Si tiene campo priority directamente (estructura de ClickUp)
      if (priority.priority) {
        switch (priority.priority.toLowerCase()) {
          case 'urgent': return 'Urgente';
          case 'high': return 'Alta';
          case 'normal': return 'Normal';
          case 'low': return 'Baja';
          default: return 'Normal';
        }
      }
      
      // Si tiene priority_name, traducirlo si está en inglés
      if (priority.priority_name) {
        const nameLower = priority.priority_name.toLowerCase();
        if (nameLower === 'urgent') return 'Urgente';
        if (nameLower === 'high') return 'Alta';
        if (nameLower === 'normal') return 'Normal';
        if (nameLower === 'low') return 'Baja';
        
        // Si no es ninguno de los anteriores, retornar el valor original
        return priority.priority_name;
      }
      
      // De lo contrario, inferir por id
      if (priority.id) {
        switch (Number(priority.id)) {
          case 1: return 'Urgente';
          case 2: return 'Alta';
          case 3: return 'Normal';
          case 4: return 'Baja';
          default: return 'Normal';
        }
      }
    }
    
    // Si es texto directo, traducirlo
    if (typeof priority === 'string') {
      const priorityLower = priority.toLowerCase();
      if (priorityLower === 'urgent') return 'Urgente';
      if (priorityLower === 'high') return 'Alta';
      if (priorityLower === 'normal') return 'Normal';
      if (priorityLower === 'low') return 'Baja';
    }
    
    return 'Normal'; // Valor predeterminado
  };
  
  // Actualizar la función handleAuthorizeTask
  const handleAuthorizeTask = async (taskId) => {
    if (!taskId) return;
    
    // Mostrar el diálogo de confirmación
    setConfirmDialogOpen(true);
  };
  
  // Función para proceder con la autorización después de la confirmación
  const proceedWithAuthorization = async () => {
    const taskId = selectedTask.id;
    
    // Limpiar errores anteriores
    setAuthorizeError(null);
    setIsAuthorizing(true);
    
    // Cerrar el diálogo de confirmación
    setConfirmDialogOpen(false);
    
    try {
      // Llamar al servicio de autorización
      const response = await authorizeTask(taskId);
      
      // Actualizar la tarea seleccionada con el nuevo estado
      setSelectedTask(prevTask => ({
        ...prevTask,
        status: response.task.status,
        keplerInfo: response.kepler // Guardar la información de Kepler
      }));
      
      // Actualizar también la tarea en la lista
      setTasks(prevTasks => 
        prevTasks.map(task => 
          task.id === taskId 
            ? {...task, status: response.task.status, keplerInfo: response.kepler}
            : task
        )
      );
      
      // Guardar la respuesta de Kepler para mostrarla en el diálogo
      setKeplerResult(response.kepler);
      
      // Mostrar el diálogo de resultado de Kepler en lugar de un alert
      setKeplerResultDialog(true);
      
      // Cerrar el diálogo después de autorizar
      handleCloseDialog();
      
      // Opcional: recargar la lista para reflejar los cambios
      getListTasks(listId, decodedStatus, { includeCompleted: showCompletedTasks })
        .then(response => {
          setTasks(response.tasks);
        })
        .catch(err => {
          console.error('Error al recargar la lista después de autorizar:', err);
        });
      
    } catch (error) {
      console.error('Error al autorizar la tarea:', error);
      setAuthorizeError(
        error.response?.data?.message || 
        'Error al autorizar la tarea. Intente nuevamente.'
      );
      
      // Mostrar mensaje de error al usuario
      alert(`Error al autorizar la tarea: ${error.response?.data?.message || 'Error desconocido'}`);
    } finally {
      setIsAuthorizing(false);
    }
  };
  
  // Función para cancelar la autorización
  const cancelAuthorization = () => {
    setConfirmDialogOpen(false);
  };

  // ----------- Flujo Aprobar / Denegar (estado Solicitado) -----------

  const handleOpenApproveDialog = () => {
    setApprovalError(null);
    setApproveDialogOpen(true);
  };

  const handleOpenDenyDialog = () => {
    setApprovalError(null);
    setDenyReason('');
    setDenyDialogOpen(true);
  };

  const handleCloseApprovalDialogs = () => {
    if (isProcessingApproval) return;
    setApproveDialogOpen(false);
    setDenyDialogOpen(false);
  };

  const refreshTasksAfterApproval = () => {
    getListTasks(listId, decodedStatus, { includeCompleted: showCompletedTasks })
      .then(response => {
        setTasks(response.tasks || []);
      })
      .catch(err => {
        console.error('Error al recargar la lista despues de aprobar/denegar:', err);
      });
  };

  const proceedWithApprove = async () => {
    if (!selectedTask) return;
    setApprovalError(null);
    setIsProcessingApproval(true);
    try {
      const response = await approveRequest(selectedTask.id);
      const newStatus = response.newStatus || 'Aprobado';

      setSelectedTask(prev => prev ? { ...prev, status: newStatus } : prev);
      setTasks(prev => prev.map(t => t.id === selectedTask.id ? { ...t, status: newStatus } : t));

      setApprovalSnackbar({
        open: true,
        message: `Solicitud aprobada. La tarea cambio al estado "${newStatus}".`,
        severity: 'success'
      });

      setApproveDialogOpen(false);
      handleCloseDialog();
      refreshTasksAfterApproval();
    } catch (error) {
      console.error('Error al aprobar la solicitud:', error);
      setApprovalError(
        error.response?.data?.message || 'Error al aprobar la solicitud. Intente nuevamente.'
      );
    } finally {
      setIsProcessingApproval(false);
    }
  };

  const proceedWithDeny = async () => {
    if (!selectedTask) return;
    const reason = denyReason.trim();
    if (!reason) {
      setApprovalError('Debe ingresar un motivo para denegar la solicitud.');
      return;
    }
    setApprovalError(null);
    setIsProcessingApproval(true);
    try {
      const response = await denyRequest(selectedTask.id, reason);
      const newStatus = response.newStatus || 'Denegado';

      setSelectedTask(prev => prev ? { ...prev, status: newStatus } : prev);
      setTasks(prev => prev.map(t => t.id === selectedTask.id ? { ...t, status: newStatus } : t));

      setApprovalSnackbar({
        open: true,
        message: `Solicitud denegada. La tarea cambio al estado "${newStatus}".`,
        severity: 'success'
      });

      setDenyDialogOpen(false);
      setDenyReason('');
      handleCloseDialog();
      refreshTasksAfterApproval();
    } catch (error) {
      console.error('Error al denegar la solicitud:', error);
      setApprovalError(
        error.response?.data?.message || 'Error al denegar la solicitud. Intente nuevamente.'
      );
    } finally {
      setIsProcessingApproval(false);
    }
  };

  // Añadir función para mostrar el diálogo de requisitos
  const handleShowRequirements = (event) => {
    event.stopPropagation(); // Prevenir que el evento se propague al diálogo principal
    setShowRequirementsDialog(true);
  };

  // Función para cerrar el diálogo de requisitos
  const handleCloseRequirements = () => {
    setShowRequirementsDialog(false);
  };

  // Función para cerrar el diálogo de resultado de Kepler
  const handleCloseKeplerResult = () => {
    setKeplerResultDialog(false);
  };

  // Componente personalizado para mostrar la prioridad
  const PriorityChip = ({ priority }) => {
    // Determinar el valor numérico de la prioridad primero
    let priorityValue;
    
    if (typeof priority === 'number') {
      priorityValue = priority;
    } else if (priority && typeof priority === 'object') {
      if (priority.id) {
        priorityValue = Number(priority.id);
      } else if (priority.priority) {
        const p = priority.priority.toLowerCase();
        if (p === 'urgent') priorityValue = 1;
        else if (p === 'high') priorityValue = 2;
        else if (p === 'normal') priorityValue = 3;
        else if (p === 'low') priorityValue = 4;
        else priorityValue = 3; // Default to normal
      } else {
        // Para cualquier otro formato, asignamos valor por defecto
        priorityValue = 3; // Default to normal
      }
    } else if (priority && typeof priority === 'string') {
      const p = priority.toLowerCase();
      if (p === 'urgent' || p === 'urgente') priorityValue = 1;
      else if (p === 'high' || p === 'alta') priorityValue = 2;
      else if (p === 'normal') priorityValue = 3;
      else if (p === 'low' || p === 'baja') priorityValue = 4;
      else priorityValue = 3; // Default to normal
    } else {
      priorityValue = 3; // Default to normal if undefined
    }
    
    // Ahora determinamos el color y etiqueta basado en el valor numérico
    let color, label;
    
    switch (priorityValue) {
      case 1:
        color = 'error';
        label = 'Urgente';
        break;
      case 2:
        color = 'warning';
        label = 'Alta';
        break;
      case 3:
        color = 'success';
        label = 'Normal';
        break;
      case 4:
        color = 'info';
        label = 'Baja';
        break;
      default:
        color = 'success';
        label = 'Normal';
    }
    
    // Ícono
    let icon = <PriorityHighIcon />;
    
    return (
      <Chip
        icon={icon}
        label={label}
        size="small"
        color={color}
        sx={{ mr: 1, mb: 1 }}
      />
    );
  };

  // Función para cargar subtareas
  const fetchTaskSubtasks = async (taskId) => {
    try {
      setLoadingSubtasks(true);
      setSubtasksError('');
      const response = await getTaskSubtasks(taskId);
      setTaskSubtasks(response.subtasks || []);
      return response.subtasks || [];
    } catch (err) {
      console.error('Error al obtener subtareas:', err);
      setSubtasksError('No se pudieron cargar las subtareas. ' + (err.response?.data?.message || err.message));
      return [];
    } finally {
      setLoadingSubtasks(false);
    }
  };

  // Función para abrir el diálogo de subtareas
  const handleOpenSubtasksDialog = async () => {
    if (selectedTask) {
      setOpenSubtasksDialog(true);
      // SIEMPRE recargar subtareas para obtener los campos actualizados (incluyendo Monto Consumido)
      if (!loadingSubtasks) {
        await fetchTaskSubtasks(selectedTask.id);
      }
    }
  };

  // Función para cerrar el diálogo de subtareas
  const handleCloseSubtasksDialog = () => {
    setOpenSubtasksDialog(false);
  };

  // Función para manejar la confirmación de recepción
  const handleConfirmReception = async (taskId) => {
    if (!taskId) return;
    
    // Mostrar el diálogo de confirmación
    setConfirmReceptionDialogOpen(true);
  };
  
  // Función para proceder con la confirmación de recepción después de confirmar
  const proceedWithReceptionConfirmation = async () => {
    const taskId = selectedTask.id;
    
    // Limpiar errores anteriores
    setConfirmReceptionError(null);
    setIsConfirmingReception(true);
    
    // Cerrar el diálogo de confirmación
    setConfirmReceptionDialogOpen(false);
    
    try {
      // Llamar al servicio de confirmación de recepción
      const response = await confirmReceptionTask(taskId);
      
      // Actualizar la tarea seleccionada con el nuevo estado
      setSelectedTask(prevTask => ({
        ...prevTask,
        status: response.status
      }));
      
      // Actualizar también la tarea en la lista
      setTasks(prevTasks => 
        prevTasks.map(task => 
          task.id === taskId 
            ? {...task, status: response.status}
            : task
        )
      );
      
      // Mostrar un mensaje de éxito
      alert(`La tarea "${selectedTask.name}" ha sido confirmada como recibida y cambiada a estado "${response.status}"`);
      
      // Cerrar el diálogo después de la confirmación
      handleCloseDialog();
      
      // Opcional: recargar la lista para reflejar los cambios
      getListTasks(listId, decodedStatus, { includeCompleted: showCompletedTasks })
        .then(response => {
          setTasks(response.tasks);
        })
        .catch(err => {
          console.error('Error al recargar la lista después de confirmar recepción:', err);
        });
      
    } catch (error) {
      console.error('Error al confirmar recepción de la tarea:', error);
      setConfirmReceptionError(
        error.response?.data?.message || 
        'Error al confirmar recepción de la tarea. Intente nuevamente.'
      );
      
      // Mostrar mensaje de error al usuario
      alert(`Error al confirmar recepción de la tarea: ${error.response?.data?.message || 'Error desconocido'}`);
    } finally {
      setIsConfirmingReception(false);
    }
  };
  
  // Función para cancelar la confirmación de recepción
  const cancelReceptionConfirmation = () => {
    setConfirmReceptionDialogOpen(false);
  };

  const visibleTasks = useMemo(() => {
    return tasks.filter((task) => {
      if (!task || !task.status) return false;
      return task.status.toLowerCase() === decodedStatus.toLowerCase();
    });
  }, [tasks, decodedStatus]);

  const handleShowCompletedTasksChange = (event) => {
    const checked = event.target.checked;
    setShowCompletedTasks(checked);

    const nextSearchParams = new URLSearchParams(searchParams);
    if (checked) {
      nextSearchParams.set('includeCompleted', 'true');
    } else {
      nextSearchParams.delete('includeCompleted');
    }

    setSearchParams(nextSearchParams, { replace: true });
  };

  return (
    <Box sx={{ p: isMobile ? 1 : 0 }}>
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/')}
          sx={{ color: 'text.secondary' }}
          size="small"
        >
          Volver al resumen
        </Button>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, flexDirection: { xs: 'column', sm: 'row' }, gap: 1, mb: 1 }}>
        <Box>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
            {decodedStatus}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Lista: <strong>{decodedListName}</strong>
          </Typography>
        </Box>
        {!isLoading && !error && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <FormControlLabel
              control={
                <Switch
                  checked={showCompletedTasks}
                  onChange={handleShowCompletedTasksChange}
                  color="secondary"
                />
              }
              label="Mostrar completadas"
            />
            <Chip
              label={`${visibleTasks.length} ${visibleTasks.length === 1 ? 'tarea' : 'tareas'}`}
              color="primary"
              sx={{ fontWeight: 700 }}
            />
          </Box>
        )}
      </Box>

      {isLoading && (
        <Grid container spacing={2} sx={{ mt: 2 }}>
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Grid item xs={12} sm={6} md={4} key={i}>
              <Card sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Box sx={{ width: 40, height: 40, borderRadius: '50%', bgcolor: 'grey.100' }} />
                  <Box sx={{ flexGrow: 1 }}>
                    <Box sx={{ height: 14, width: '70%', bgcolor: 'grey.100', borderRadius: 1, mb: 0.5 }} />
                    <Box sx={{ height: 12, width: '40%', bgcolor: 'grey.100', borderRadius: 1 }} />
                  </Box>
                </Box>
                <Box sx={{ height: 80, bgcolor: 'grey.100', borderRadius: 1, mt: 1 }} />
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
      
      {error && (
        <Alert severity="error" sx={{ my: 2 }}>{error}</Alert>
      )}
      
      {/* Tarjetas de tareas */}
      {!isLoading && !error && visibleTasks.length > 0 && (
        <Grid container spacing={2} sx={{ mt: 1 }}>
          {visibleTasks.map((task) => {
            // Renderizar de forma segura, capturando posibles errores por tarea
            try {
              if (!task || !task.status) {
                console.warn('Tarea inválida encontrada:', task);
                return null;
              }
              return (
                <Grid item xs={12} sm={6} md={4} key={task.id || `task-${Math.random()}`}>
                  <ErrorBoundary> {/* Wrap each card */}
                    {renderTaskCard(task)}
                  </ErrorBoundary>
                </Grid>
              );
            } catch (err) {
              console.error('Error al renderizar tarea:', err, task);
              return (
                <Grid item xs={12} sm={6} md={4} key={task.id || `task-error-${Math.random()}`}>
                  <Card sx={{ p: 2, bgcolor: 'warning.light', color: 'warning.contrastText' }}>
                    <Typography color="error" gutterBottom>
                      Error al mostrar esta tarea
                    </Typography>
                    <Typography variant="body2">
                      ID: {task.id || 'No disponible'}
                    </Typography>
                    {task.name && (
                      <Typography variant="body2" noWrap>
                        {task.name}
                      </Typography>
                    )}
                  </Card>
                </Grid>
              );
            }
          })}
        </Grid>
      )}
      
      {/* Mensaje de no hay tareas */}
      {!isLoading && !error && visibleTasks.length === 0 && (
        <Alert severity="info" sx={{ my: 2 }}>
          No hay tareas en la categoría seleccionada.
        </Alert>
      )}
      
      {/* Botón para refrescar */}
      {!isLoading && (
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
          <Button 
            variant="contained" 
            color="primary"
            onClick={() => {
              setIsLoading(true);
              setError('');
              // Si hay una tarea seleccionada, también actualizamos sus adjuntos
              const selectedTaskId = selectedTask?.id;
              
              getListTasks(listId, decodedStatus, { includeCompleted: showCompletedTasks })
                .then(response => {
                  setTasks(response.tasks);
                  setListData({
                    listId: response.listId,
                    listName: response.listName,
                    totalTasks: response.totalTasks
                  });
                  
                  // Si hay un diálogo abierto con una tarea, actualizamos esa tarea también
                  if (selectedTaskId && openDialog) {
                    const updatedTask = response.tasks.find(task => task.id === selectedTaskId);
                    if (updatedTask) {
                      setSelectedTask(updatedTask);
                      
                      // Volvemos a cargar los adjuntos
                      setLoadingAttachments(true);
                      setAttachmentError('');
                      getTaskAttachments(selectedTaskId)
                        .then(attachResponse => {
                          setTaskAttachments(attachResponse.attachments || []);
                          // Si la tarea tiene URL para ver adjuntos, actualizamos la tarea seleccionada con ella
                          if (attachResponse.viewAttachmentsUrl) {
                            setSelectedTask(prevTask => ({
                              ...prevTask,
                              viewAttachmentsUrl: attachResponse.viewAttachmentsUrl
                            }));
                          }
                        })
                        .catch(err => {
                          console.error('Error al refrescar adjuntos:', err);
                          setAttachmentError('Error al refrescar los adjuntos. Intente de nuevo.');
                        })
                        .finally(() => setLoadingAttachments(false));
                    }
                  }
                })
                .catch(err => {
                  console.error('Error al refrescar tareas:', err);
                  setError('Error al refrescar datos. Intente de nuevo.');
                })
                .finally(() => setIsLoading(false));
            }}
          >
            Refrescar datos
          </Button>
        </Box>
      )}

      {/* Modal de detalles de la tarea */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        fullScreen={fullScreen}
        maxWidth="md"
        fullWidth
        aria-labelledby="task-details-dialog"
      >
        {selectedTask && (
          <>
            <DialogTitle sx={{ 
              bgcolor: getStatusBackgroundColor(selectedTask.status, selectedTask.statusType),
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: 2
            }}>
              <Box>
                <Typography variant="h6" component="h2" sx={{ fontWeight: 'bold' }}>
                  {selectedTask.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  ID: {selectedTask.id}
                </Typography>
              </Box>
              <IconButton edge="end" color="inherit" onClick={handleCloseDialog} aria-label="close">
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <Divider />
            <DialogContent>
              <Grid container spacing={3}>
                {/* Estado */}
                <Grid item xs={12} sm={6}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                      Estado
                    </Typography>
                    <Chip 
                      label={selectedTask.status} 
                      color={theme.palette[getStatusColor(selectedTask.status, selectedTask.statusType)]?.main 
                        ? getStatusColor(selectedTask.status, selectedTask.statusType) 
                        : 'primary'}
                      sx={{ fontWeight: 'medium' }}
                    />
                  </Box>
                </Grid>

                {/* Montos y Valores */}
                <Grid item xs={12} sm={6}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                      Monto de la Orden
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <MonetizationOnIcon fontSize="medium" color="success" sx={{ mr: 1 }} />
                      {selectedTask.montoOrden && (typeof selectedTask.montoOrden.valor === 'number' ? selectedTask.montoOrden.valor !== 0 : selectedTask.montoOrden.valor) ? (
                        <Typography variant="h6" color="success.main" fontWeight="bold">
                          {selectedTask.montoOrden.descripcion ? (
                            selectedTask.montoOrden.descripcion
                          ) : typeof selectedTask.montoOrden.valor === 'number' ? (
                            `$${selectedTask.montoOrden.valor.toLocaleString('es-MX')}`
                          ) : (
                            selectedTask.montoOrden.valor || 'N/A'
                          )}
                        </Typography>
                      ) : (
                        <Typography variant="body1" color="text.secondary">
                          No disponible
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Grid>

                {/* Monto Consumido */}
                {selectedTask.montoConsumido && (
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                        Monto Consumido
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <MonetizationOnIcon fontSize="medium" color="warning" sx={{ mr: 1 }} />
                        <Typography variant="h6" color="warning.main" fontWeight="bold">
                          ${parseFloat(selectedTask.montoConsumido.valor).toLocaleString('es-MX')}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                )}

                {/* Tipo de Orden */}
                <Grid item xs={12} sm={6}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                      Tipo de Orden
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <CategoryIcon fontSize="medium" color="primary" sx={{ mr: 1 }} />
                      {selectedTask.tipoOrden ? (
                        <Typography variant="body1" fontWeight="medium">
                          {selectedTask.tipoOrden.descripcion || selectedTask.tipoOrden.valor || 'N/A'}
                          {!selectedTask.tipoOrden.descripcion && selectedTask.tipoOrden.valor !== null && (
                            <Typography variant="caption" color="text.secondary" sx={{ ml: 1, display: 'block' }}>
                              (ID: {selectedTask.tipoOrden.campo_id}, Valor: {selectedTask.tipoOrden.valor})
                            </Typography>
                          )}
                        </Typography>
                      ) : (
                        <Typography variant="body1" color="text.secondary">
                          No disponible
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Grid>

                {/* Forma de Pago */}
                <Grid item xs={12} sm={6}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                      Forma de Pago
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <PaymentIcon fontSize="medium" color="info" sx={{ mr: 1 }} />
                      {selectedTask.formaPago ? (
                        <Typography variant="body1" fontWeight="medium">
                          {selectedTask.formaPago.descripcion || selectedTask.formaPago.valor || 'N/A'}
                        </Typography>
                      ) : (
                        <Typography variant="body1" color="text.secondary">
                          No disponible
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Grid>
                
                {/* Módulo */}
                <Grid item xs={12} sm={6}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                      Módulo
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <ViewModuleIcon fontSize="medium" color="secondary" sx={{ mr: 1 }} />
                      {selectedTask.modulo ? (
                        <Typography variant="body1" fontWeight="medium">
                          {selectedTask.modulo.descripcion || selectedTask.modulo.valor || 'N/A'}
                          {!selectedTask.modulo.descripcion && selectedTask.modulo.valor !== null && (
                            <Typography variant="caption" color="text.secondary" sx={{ ml: 1, display: 'block' }}>
                              (ID: {selectedTask.modulo.campo_id}, Valor: {selectedTask.modulo.valor})
                            </Typography>
                          )}
                        </Typography>
                      ) : (
                        <Typography variant="body1" color="text.secondary">
                          No disponible
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Grid>

                {/* Prioridad */}
                <Grid item xs={12} sm={6}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                      Prioridad
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
                      <PriorityChip priority={selectedTask.priority} />
                      
                      <FormControl size="small" sx={{ minWidth: 120 }}>
                        <Select
                          value={(() => {
                            // Esta función garantiza que el valor sea numérico
                            if (selectedTask.priority) {
                              if (typeof selectedTask.priority === 'number') {
                                return selectedTask.priority;
                              } else if (typeof selectedTask.priority === 'object') {
                                if (selectedTask.priority.id) {
                                  return Number(selectedTask.priority.id);
                                } else if (selectedTask.priority.priority) {
                                  const p = selectedTask.priority.priority.toLowerCase();
                                  if (p === 'urgent') return 1;
                                  else if (p === 'high') return 2;
                                  else if (p === 'normal') return 3;
                                  else if (p === 'low') return 4;
                                }
                              } else if (typeof selectedTask.priority === 'string') {
                                const p = selectedTask.priority.toLowerCase();
                                if (p === 'urgent' || p === 'urgente') return 1;
                                else if (p === 'high' || p === 'alta') return 2;
                                else if (p === 'normal') return 3;
                                else if (p === 'low' || p === 'baja') return 4;
                              }
                            }
                            return 3; // Valor predeterminado (Normal)
                          })()}
                          onChange={handlePriorityChange}
                          disabled={isUpdatingPriority || isReadOnlyUser}
                          sx={{ height: '36px' }}
                          renderValue={(value) => {
                            // Si tenemos priorityLabel, usarlo directamente
                            if (selectedTask.priorityLabel) {
                              return selectedTask.priorityLabel;
                            }
                            
                            // De lo contrario, inferir por el valor
                            return getPriorityLabel(value);
                          }}
                        >
                          <MenuItem value={1}><PriorityHighIcon sx={{ mr: 1, color: 'error.main' }} />Urgente</MenuItem>
                          <MenuItem value={2}><PriorityHighIcon sx={{ mr: 1, color: 'warning.main' }} />Alta</MenuItem>
                          <MenuItem value={3}><PriorityHighIcon sx={{ mr: 1, color: 'success.main' }} />Normal</MenuItem>
                          <MenuItem value={4}><PriorityHighIcon sx={{ mr: 1, color: 'info.main' }} />Baja</MenuItem>
                        </Select>
                      </FormControl>
                      
                      {isUpdatingPriority && (
                        <CircularProgress size={20} sx={{ ml: 1 }} />
                      )}
                      
                      {priorityUpdateError && (
                        <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block', width: '100%' }}>
                          {priorityUpdateError}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Grid>

                {/* Avance */}
                {selectedTask.avance && (
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                        {selectedTask.avance.nombre || 'Avance'}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <TrendingUpIcon fontSize="medium" color="primary" sx={{ mr: 1 }} />
                        <Box sx={{ width: '100%' }}>
                           <LinearProgressWithLabel value={selectedTask.avance.valor || 0} />
                        </Box>
                      </Box>
                    </Box>
                  </Grid>
                )}

                {/* Fechas */}
                <Grid item xs={12} sm={6}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                      Fechas
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <EventIcon fontSize="small" color="action" sx={{ mr: 1 }} />
                      <Typography variant="body2">
                        <strong>Creación:</strong> {formatDateTime(selectedTask.dateCreated)}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <CalendarTodayIcon fontSize="small" color="action" sx={{ mr: 1 }} />
                      <Typography variant="body2">
                        <strong>Fecha límite:</strong> {formatDateTime(selectedTask.dueDate)}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                {/* Descripción / Explicación detallada de la tarea (descripción nativa de ClickUp) */}
                <Grid item xs={12}>
                  <Box sx={{ mb: 2, mt: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
                        <DescriptionIcon fontSize="small" sx={{ mr: 1 }} />
                        Descripción / Explicación detallada
                      </Typography>
                      {selectedTask.description && selectedTask.description.length > 400 && (
                        <Button
                          size="small"
                          onClick={() => setDescriptionExpanded(prev => !prev)}
                          sx={{ textTransform: 'none' }}
                        >
                          {descriptionExpanded ? 'Ver menos' : 'Ver más'}
                        </Button>
                      )}
                    </Box>
                    <Paper elevation={0} sx={{
                      p: 2,
                      bgcolor: 'rgba(0,0,0,0.02)',
                      borderRadius: 1,
                      maxHeight: descriptionExpanded ? 'none' : '260px',
                      overflow: 'auto',
                      border: '1px solid rgba(0,0,0,0.08)'
                    }}>
                      {selectedTask.description ? (
                        <Typography
                          variant="body2"
                          component="div"
                          sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                        >
                          {selectedTask.description}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary" fontStyle="italic">
                          Esta tarea no tiene una explicación detallada registrada en ClickUp.
                        </Typography>
                      )}
                    </Paper>
                  </Box>
                </Grid>

                {/* Asignados */}
                <Grid item xs={12} sm={6}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                      Asignados
                    </Typography>
                    {selectedTask.assignees && selectedTask.assignees.length > 0 ? (
                      <List dense disablePadding>
                        {selectedTask.assignees.map((assignee, index) => (
                          <ListItem key={index} disablePadding sx={{ mb: 0.5 }}>
                            <ListItemIcon sx={{ minWidth: '36px' }}>
                              <Avatar sx={{ width: 24, height: 24 }}>
                                {assignee.charAt(0).toUpperCase()}
                              </Avatar>
                            </ListItemIcon>
                            <ListItemText primary={assignee} />
                          </ListItem>
                        ))}
                      </List>
                    ) : (
                      <Typography variant="body2">Sin asignar</Typography>
                    )}
                  </Box>
                </Grid>

                {/* Tags/Etiquetas */}
                {selectedTask.tags && selectedTask.tags.length > 0 && (
                  <Grid item xs={12}>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                        Etiquetas
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {selectedTask.tags.map((tag, index) => (
                          <Chip 
                            key={index}
                            icon={<LabelIcon />}
                            label={tag}
                            size="small"
                            variant="outlined"
                          />
                        ))}
                      </Box>
                    </Box>
                  </Grid>
                )}

                {/* Documentos adjuntos */}
                <Grid item xs={12}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <AttachFileIcon fontSize="small" sx={{ mr: 1 }} />
                        Documentos Adjuntos
                        {taskAttachments.length > 0 && (
                          <Chip 
                            label={taskAttachments.length} 
                            color="primary" 
                            size="small" 
                            sx={{ ml: 1 }}
                          />
                        )}
                      </Box>
                    </Typography>
            
                    {renderAttachmentCards(selectedTask)}
                  </Box>
                </Grid>

                {/* Comentarios / Actividad */}
                <Grid item xs={12}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <ChatBubbleOutlineIcon fontSize="small" sx={{ mr: 1 }} />
                        Comentarios / Actividad
                        {taskComments.length > 0 && (
                          <Chip
                            label={taskComments.length}
                            color="primary"
                            size="small"
                            sx={{ ml: 1 }}
                          />
                        )}
                      </Box>
                    </Typography>

                    {loadingComments ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', py: 2 }}>
                        <CircularProgress size={24} sx={{ mr: 2 }} />
                        <Typography variant="body2" color="text.secondary">
                          Cargando comentarios...
                        </Typography>
                      </Box>
                    ) : commentsError ? (
                      <Alert severity="warning" sx={{ mb: 2 }}>
                        {commentsError}
                        {viewCommentsUrl && (
                          <Box sx={{ mt: 1 }}>
                            <Button
                              size="small"
                              href={viewCommentsUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              Ver en ClickUp
                            </Button>
                          </Box>
                        )}
                      </Alert>
                    ) : taskComments.length === 0 ? (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Esta tarea aún no tiene comentarios.
                      </Typography>
                    ) : (
                      <Paper
                        elevation={0}
                        sx={{
                          mb: 2,
                          maxHeight: 320,
                          overflow: 'auto',
                          border: '1px solid rgba(0,0,0,0.08)',
                          borderRadius: 1
                        }}
                      >
                        <List dense disablePadding>
                          {taskComments.map((comment) => (
                            <ListItem
                              key={comment.id}
                              alignItems="flex-start"
                              sx={{
                                px: 2,
                                py: 1.5,
                                borderBottom: '1px solid rgba(0,0,0,0.06)'
                              }}
                            >
                              <ListItemIcon sx={{ minWidth: 40, mt: 0.5 }}>
                                <Avatar sx={{ width: 32, height: 32 }}>
                                  {getCommentAuthorInitial(comment)}
                                </Avatar>
                              </ListItemIcon>
                              <ListItemText
                                primary={(
                                  <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: 1 }}>
                                    <Typography variant="subtitle2" component="span">
                                      {getCommentAuthorLabel(comment)}
                                    </Typography>
                                    {comment.keplerAuthor?.email && (
                                      <Typography variant="caption" color="text.secondary" component="span">
                                        {comment.keplerAuthor.email}
                                      </Typography>
                                    )}
                                    <Typography variant="caption" color="text.secondary" component="span">
                                      {formatDateTime(comment.date)}
                                    </Typography>
                                  </Box>
                                )}
                                secondary={(
                                  <Typography
                                    variant="body2"
                                    component="span"
                                    sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', display: 'block', mt: 0.5 }}
                                  >
                                    {comment.commentText || comment.fullText}
                                  </Typography>
                                )}
                              />
                            </ListItem>
                          ))}
                        </List>
                      </Paper>
                    )}

                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        border: '1px solid rgba(0,0,0,0.08)',
                        borderRadius: 1,
                        bgcolor: 'rgba(0,0,0,0.02)'
                      }}
                    >
                      <TextField
                        fullWidth
                        multiline
                        minRows={3}
                        maxRows={8}
                        placeholder="Escribe un comentario..."
                        value={newCommentText}
                        onChange={(event) => setNewCommentText(event.target.value)}
                        disabled={isPostingComment}
                      />
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1.5, gap: 1, flexWrap: 'wrap' }}>
                        <Typography variant="caption" color="text.secondary">
                          {`Se publicará como ${user?.nombre || user?.name || user?.email || 'tu cuenta Kepler'}.`}
                        </Typography>
                        <Button
                          variant="contained"
                          endIcon={isPostingComment ? <CircularProgress size={18} color="inherit" /> : <SendIcon />}
                          onClick={handlePostComment}
                          disabled={isPostingComment || !newCommentText.trim()}
                        >
                          {isPostingComment ? 'Publicando...' : 'Comentar'}
                        </Button>
                      </Box>
                      {postCommentError && (
                        <Alert severity="error" sx={{ mt: 2 }}>
                          {postCommentError}
                          {viewCommentsUrl && (
                            <Box sx={{ mt: 1 }}>
                              <Button
                                size="small"
                                href={viewCommentsUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                Ver en ClickUp
                              </Button>
                            </Box>
                          )}
                        </Alert>
                      )}
                    </Paper>
                  </Box>
                </Grid>

                {/* Tipo de Cambio */}
                <Grid item xs={12} sm={6}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                      Tipo de Cambio
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <MonetizationOnIcon fontSize="medium" color="info" sx={{ mr: 1 }} />
                      <Box>
                        {(() => {
                          // Lógica para determinar qué tipo de cambio mostrar y su origen
                          const taskRate = selectedTask.tipoCambio && selectedTask.tipoCambio.valor ? parseFloat(selectedTask.tipoCambio.valor) : null;
                          const sessionRate = exchangeRate ? parseFloat(exchangeRate) : null;
                          const sessionRateDate = exchangeRateDate;
                          
                          // 1. Si la tarea tiene tipo de cambio válido (mayor que 0)
                          if (taskRate && taskRate > 0) {
                            return (
                              <Box>
                                <Typography variant="h6" color="primary.main" fontWeight="bold">
                                  ${taskRate.toLocaleString('es-MX', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  Tipo de cambio de la tarea
                                </Typography>
                              </Box>
                            );
                          }
                          
                          // 2. Si no hay TC en tarea, verificar TC del proyecto (sesión)
                          if (sessionRate && sessionRateDate) {
                            try {
                              const [y, m, d] = sessionRateDate.split('-').map(Number);
                              if (y && m && d) {
                                const today = new Date();
                                today.setHours(0,0,0,0);
                                const expiry = new Date(y, m - 1, d);
                                expiry.setHours(0,0,0,0);
                                
                                if (today <= expiry) {
                                  return (
                                    <Box>
                                      <Typography variant="h6" color="info.main" fontWeight="bold">
                                        ${sessionRate.toLocaleString('es-MX', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary">
                                        TC del proyecto (vigente hasta {sessionRateDate})
                                      </Typography>
                                    </Box>
                                  );
                                } else {
                                  return (
                                    <Box>
                                      <Typography variant="h6" color="warning.main" fontWeight="bold">
                                        Se consultará Banxico DOF
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary">
                                        TC del proyecto expirado el {sessionRateDate}
                                      </Typography>
                                    </Box>
                                  );
                                }
                              }
                            } catch (err) {
                              // Error al parsear fecha
                            }
                          }
                          
                          // 3. Si no hay TC válido de tarea ni proyecto, se usará Banxico
                          return (
                            <Box>
                              <Typography variant="h6" color="warning.main" fontWeight="bold">
                                Se consultará Banxico DOF
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Se obtendrá automáticamente al autorizar
                              </Typography>
                            </Box>
                          );
                        })()}
                      </Box>
                    </Box>
                  </Box>
                </Grid>

                {/* Subtareas - Nuevo componente */}
                <Grid item xs={12} sm={6}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                      Subtareas
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <SubtasksIcon fontSize="medium" color="info" sx={{ mr: 1 }} />
                      {selectedTask.subtasksCount !== undefined ? (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Chip 
                            label={`${selectedTask.subtasksCount} subtarea${selectedTask.subtasksCount !== 1 ? 's' : ''}`}
                            color={selectedTask.subtasksCount > 0 ? "primary" : "default"}
                            onClick={selectedTask.subtasksCount > 0 ? handleOpenSubtasksDialog : undefined}
                            clickable={selectedTask.subtasksCount > 0}
                          />
                        </Box>
                      ) : loadingSubtasks ? (
                        <CircularProgress size={20} sx={{ ml: 1 }} />
                      ) : (
                        <Typography variant="body1" color="text.secondary">
                          No disponible
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button 
                  onClick={handleCloseDialog} 
                  color="primary"
                  variant="outlined"
                >
                  Cerrar
                </Button>
              </Box>
              
              <Box sx={{ display: 'flex', gap: 1 }}>
                {/* Botón de autorización */}
                {selectedTask && selectedTask.status && selectedTask.status.toLowerCase().includes('por autorizar') && (
                  <>
                    {canAuthorizeTask(selectedTask).canAuthorize ? (
                      <Button 
                        color="success" 
                        variant="contained"
                        onClick={() => handleAuthorizeTask(selectedTask.id)}
                        disabled={isAuthorizing || isReadOnlyUser}
                        startIcon={isAuthorizing ? <CircularProgress size={20} color="inherit" /> : <CheckCircleIcon />}
                      >
                        {isAuthorizing ? 'Autorizando...' : 'Autorizar'}
                      </Button>
                    ) : (
                      <Button 
                        color="error" 
                        variant="contained"
                        startIcon={<WarningIcon />}
                        onClick={handleShowRequirements}
                        sx={{ 
                          bgcolor: 'error.main',
                          color: 'error.contrastText',
                          '&:hover': {
                            bgcolor: 'error.dark',
                          }
                        }}
                      >
                        No se puede autorizar
                      </Button>
                    )}
                  </>
                )}

                {/* Botones Aprobar / Denegar para tareas en estado Solicitado */}
                {selectedTask && selectedTask.status && selectedTask.status.toLowerCase().includes('solicitado') && (
                  <>
                    <Button
                      color="success"
                      variant="contained"
                      onClick={handleOpenApproveDialog}
                      disabled={isProcessingApproval || isReadOnlyUser}
                      startIcon={<ThumbUpAltOutlinedIcon />}
                    >
                      Aprobar
                    </Button>
                    <Button
                      color="error"
                      variant="contained"
                      onClick={handleOpenDenyDialog}
                      disabled={isProcessingApproval || isReadOnlyUser}
                      startIcon={<ThumbDownAltOutlinedIcon />}
                    >
                      Denegar
                    </Button>
                  </>
                )}

                {/* Botón de confirmar recepción */}
                {selectedTask && selectedTask.status && selectedTask.status.toLowerCase().includes('confirma recepcion') && (
                  <Button 
                    color="primary" 
                    variant="contained"
                    onClick={() => handleConfirmReception(selectedTask.id)}
                    disabled={isConfirmingReception || isReadOnlyUser}
                    startIcon={isConfirmingReception ? <CircularProgress size={20} color="inherit" /> : <CheckCircleIcon />}
                    sx={{ 
                      bgcolor: 'primary.main',
                      color: 'primary.contrastText',
                      fontWeight: 'bold',
                      '&:hover': {
                        bgcolor: 'primary.dark',
                      },
                      boxShadow: 4
                    }}
                  >
                    {isConfirmingReception ? 'Confirmando...' : 'Confirmar Recepción'}
                  </Button>
                )}
              </Box>
            </DialogActions>

            {/* Modal para mostrar información de depuración */}
            <Dialog
              open={showDebugInfo}
              onClose={() => setShowDebugInfo(false)}
              fullWidth
              maxWidth="md"
            >
              <DialogTitle>
                Información de Campos Personalizados
                <IconButton
                  aria-label="close"
                  onClick={() => setShowDebugInfo(false)}
                  sx={{
                    position: 'absolute',
                    right: 8,
                    top: 8,
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </DialogTitle>
              <DialogContent dividers>
                {debugInfo ? (
                  <Box sx={{ overflow: 'auto' }}>
                    <Typography variant="h6" gutterBottom>
                      Tarea: {debugInfo.taskName}
                    </Typography>
                    <Typography variant="subtitle1" sx={{ mt: 2, fontWeight: 'bold' }}>
                      Campos Personalizados ({debugInfo.custom_fields.length})
                    </Typography>
                    
                    {debugInfo.custom_fields.map((field, index) => (
                      <Box key={index} sx={{ mb: 3, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                          {field.name} ({field.type})
                        </Typography>
                        <Typography variant="body2">
                          <strong>ID:</strong> {field.id}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Valor:</strong> {field.value !== null ? String(field.value) : 'null'}
                        </Typography>

                        {field.type === 'dropdown' && (
                          <>
                            <Typography variant="body2" sx={{ mt: 1, fontWeight: 'bold' }}>
                              Opción Seleccionada:
                            </Typography>
                            {field.selectedOption ? (
                              <Box sx={{ pl: 2 }}>
                                <Typography variant="body2">
                                  <strong>ID:</strong> {field.selectedOption.id}
                                </Typography>
                                <Typography variant="body2">
                                  <strong>OrderIndex:</strong> {field.selectedOption.orderindex}
                                </Typography>
                                <Typography variant="body2">
                                  <strong>Nombre:</strong> {field.selectedOption.name}
                                </Typography>
                              </Box>
                            ) : (
                              <Typography variant="body2" color="error">
                                No se encontró la opción seleccionada
                              </Typography>
                            )}

                            <Typography variant="body2" sx={{ mt: 1, fontWeight: 'bold' }}>
                              Opciones Disponibles:
                            </Typography>
                            {field.options.map((option, optIndex) => (
                              <Box key={optIndex} sx={{ pl: 2, py: 0.5, bgcolor: field.value === option.id ? 'rgba(0,255,0,0.1)' : 'inherit' }}>
                                <Typography variant="body2">
                                  <strong>ID:</strong> {option.id} | <strong>OrderIndex:</strong> {option.orderindex} | <strong>Nombre:</strong> {option.name}
                                </Typography>
                              </Box>
                            ))}

                            {field.options_complete && field.options_complete.length > 0 && (
                              <>
                                <Typography variant="body2" sx={{ mt: 2, fontWeight: 'bold', color: 'success.main' }}>
                                  Opciones Completas (API Field):
                                </Typography>
                                <Box sx={{ pl: 2, py: 1, bgcolor: 'rgba(0,255,0,0.05)', borderRadius: 1 }}>
                                  {field.options_complete.map((option, optIndex) => (
                                    <Box key={optIndex} sx={{ pl: 2, py: 0.5, borderLeft: field.value === option.id || String(option.orderindex) === String(field.value) ? '2px solid green' : 'none' }}>
                                      <Typography variant="body2">
                                        <strong>ID:</strong> {option.id} | <strong>OrderIndex:</strong> {option.orderindex} | <strong>Nombre:</strong> {option.name}
                                      </Typography>
                                    </Box>
                                  ))}
                                </Box>
                              </>
                            )}
                          </>
                        )}
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <CircularProgress />
                )}
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setShowDebugInfo(false)}>Cerrar</Button>
              </DialogActions>
            </Dialog>
          </>
        )}
      </Dialog>

      {/* Diálogo de confirmación de autorización */}
      <Dialog
        open={confirmDialogOpen}
        onClose={cancelAuthorization}
        maxWidth="md"
        fullWidth
        aria-labelledby="confirm-authorization-dialog"
      >
        <DialogTitle sx={{ bgcolor: 'warning.light', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" component="div" fontWeight="bold">
            Confirmar Autorización de Tarea
          </Typography>
          <IconButton edge="end" color="inherit" onClick={cancelAuthorization} aria-label="close">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 3 }}>
          {selectedTask && (
            <>
              <Alert severity="warning" sx={{ mb: 3 }}>
                Por favor revise cuidadosamente los datos de la tarea antes de autorizar.
              </Alert>
              
              <Typography variant="h6" gutterBottom>
                {selectedTask.name}
              </Typography>
              
              <Box sx={{ mt: 2, mb: 3, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                  Datos que se registrarán en Kepler:
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="textSecondary">
                      ID de Tarea (k_code)
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {selectedTask.id}
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="textSecondary">
                      Descripción (k_dscr)
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {selectedTask.name}
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="textSecondary">
                      Usuario que autoriza (c81)
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {user?.nombre || 'Usuario del sistema'}
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="textSecondary">
                      Tipo de Orden (k_type)
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {selectedTask.tipoOrden?.descripcion || 'No especificado'}
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="textSecondary">
                      Módulo (k_grp)
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {selectedTask.modulo?.descripcion || 'No especificado'}
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="textSecondary">
                      Forma de Pago (c83)
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {selectedTask.formaPago?.descripcion || 'No especificado'}
                    </Typography>
                  </Grid>
                  
                                    {selectedTask.montoConsumido && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="textSecondary">
                        Monto Consumido
                      </Typography>
                      <Typography variant="body1" fontWeight="medium" color="warning.main">
                        ${parseFloat(selectedTask.montoConsumido.valor).toLocaleString('es-MX')}
                      </Typography>
                    </Grid>
                  )}
                  
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="textSecondary">
                      Monto (c85)
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {selectedTask.montoOrden?.valor 
                        ? `$${parseFloat(selectedTask.montoOrden.valor).toLocaleString('es-MX')}`
                        : 'No especificado'}
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="textSecondary">
                      Paridad (c84)
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {(() => {
                        // Lógica para determinar qué tipo de cambio mostrar y su origen
                        const taskRate = selectedTask.tipoCambio && selectedTask.tipoCambio.valor ? parseFloat(selectedTask.tipoCambio.valor) : null;
                        const sessionRate = exchangeRate ? parseFloat(exchangeRate) : null;
                        const sessionRateDate = exchangeRateDate;
                        
                        // 1. Si la tarea tiene tipo de cambio válido (mayor que 0)
                        if (taskRate && taskRate > 0) {
                          return (
                            <Box>
                              <Typography variant="body1" fontWeight="medium" color="primary.main">
                                ${taskRate.toLocaleString('es-MX', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                (Tipo de cambio de la tarea)
                              </Typography>
                            </Box>
                          );
                        }
                        
                        // 2. Si no hay TC en tarea, verificar TC del proyecto (sesión)
                        if (sessionRate && sessionRateDate) {
                          try {
                            const [y, m, d] = sessionRateDate.split('-').map(Number);
                            if (y && m && d) {
                              const today = new Date();
                              today.setHours(0,0,0,0);
                              const expiry = new Date(y, m - 1, d);
                              expiry.setHours(0,0,0,0);
                              
                              if (today <= expiry) {
                                return (
                                  <Box>
                                    <Typography variant="body1" fontWeight="medium" color="info.main">
                                      ${sessionRate.toLocaleString('es-MX', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      (Tipo de cambio del proyecto, vigente hasta {sessionRateDate})
                                    </Typography>
                                  </Box>
                                );
                              } else {
                                return (
                                  <Box>
                                    <Typography variant="body1" fontWeight="medium" color="warning.main">
                                      Se consultará Banxico DOF
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      (TC del proyecto expirado el {sessionRateDate})
                                    </Typography>
                                  </Box>
                                );
                              }
                            }
                          } catch (err) {
                            // Error al parsear fecha
                          }
                        }
                        
                        // 3. Si no hay TC válido de tarea ni proyecto, se usará Banxico
                        return (
                          <Box>
                            <Typography variant="body1" fontWeight="medium" color="warning.main">
                              Se consultará Banxico DOF
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              (Se obtendrá automáticamente al autorizar)
                            </Typography>
                          </Box>
                        );
                      })()}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
              
              <Alert severity="info" sx={{ mb: 2 }}>
                Al autorizar, se generarán 3 hashes de seguridad que se registrarán en Kepler:
                <List dense>
                  <ListItem>
                    <ListItemIcon>
                      <LockIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Hash de Orden (c78)" 
                      secondary="Deriva de los datos de la orden, usuario y timestamp" 
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <DescriptionIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Hash de Adjuntos (c79)" 
                      secondary="Deriva del contenido de los documentos PDF adjuntos" 
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <EnhancedEncryptionIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Hash de Integración (c80)" 
                      secondary="Combina los dos hashes anteriores para garantizar integridad" 
                    />
                  </ListItem>
                </List>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  <strong>Importante:</strong> Al autorizar la orden, recibirás automáticamente un correo electrónico con todos 
                  los detalles y hashes de seguridad para tu confirmación y registro. Los documentos PDF adjuntos a la tarea 
                  también se incluirán en el correo de confirmación.
                </Typography>
              </Alert>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
          <Button 
            onClick={cancelAuthorization} 
            color="inherit"
            variant="outlined"
          >
            Cancelar
          </Button>
          <Button 
            onClick={proceedWithAuthorization} 
            color="success" 
            variant="contained"
            disabled={isAuthorizing || isReadOnlyUser}
            startIcon={isAuthorizing ? <CircularProgress size={20} color="inherit" /> : <CheckCircleIcon />}
          >
            {isAuthorizing ? 'Autorizando...' : 'Confirmar Autorización'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo de requisitos para autorización */}
      <Dialog
        open={showRequirementsDialog}
        onClose={handleCloseRequirements}
        maxWidth="sm"
        fullWidth
        aria-labelledby="requirements-dialog"
      >
        <DialogTitle sx={{ bgcolor: 'error.light', color: 'error.contrastText' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarningIcon />
            <Typography variant="h6">
              No se puede autorizar esta tarea
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ pt: 3 }}>
          {selectedTask && (
            <>
              <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                Para autorizar esta tarea, se requiere que cumpla con todos los siguientes requisitos:
              </Typography>
              
              <List>
                <ListItem
                  secondaryAction={
                    <Chip 
                      label={selectedTask.montoOrden && (typeof selectedTask.montoOrden.valor === 'number' ? selectedTask.montoOrden.valor !== 0 : selectedTask.montoOrden.valor) ? "✓" : "✗"} 
                      color={selectedTask.montoOrden && (typeof selectedTask.montoOrden.valor === 'number' ? selectedTask.montoOrden.valor !== 0 : selectedTask.montoOrden.valor) ? "success" : "error"}
                      size="small"
                    />
                  }
                >
                  <ListItemText
                    primary="Monto de la Orden"
                    secondary="La tarea debe tener un monto de orden especificado y distinto de cero"
                  />
                </ListItem>
                
                <ListItem
                  secondaryAction={
                    <Chip 
                      label={selectedTask.tipoOrden && (selectedTask.tipoOrden.valor !== undefined && selectedTask.tipoOrden.valor !== null && selectedTask.tipoOrden.valor !== '') ? "✓" : "✗"} 
                      color={selectedTask.tipoOrden && (selectedTask.tipoOrden.valor !== undefined && selectedTask.tipoOrden.valor !== null && selectedTask.tipoOrden.valor !== '') ? "success" : "error"}
                      size="small"
                    />
                  }
                >
                  <ListItemText
                    primary="Tipo de Orden"
                    secondary="La tarea debe tener un tipo de orden especificado"
                  />
                </ListItem>
                
                <ListItem
                  secondaryAction={
                    <Chip 
                      label={selectedTask.formaPago && (selectedTask.formaPago.valor !== undefined && selectedTask.formaPago.valor !== null && selectedTask.formaPago.valor !== '') ? "✓" : "✗"} 
                      color={selectedTask.formaPago && (selectedTask.formaPago.valor !== undefined && selectedTask.formaPago.valor !== null && selectedTask.formaPago.valor !== '') ? "success" : "error"}
                      size="small"
                    />
                  }
                >
                  <ListItemText
                    primary="Forma de Pago"
                    secondary="La tarea debe tener una forma de pago especificada"
                  />
                </ListItem>
                
                <ListItem
                  secondaryAction={
                    <Chip 
                      label={selectedTask.modulo && (selectedTask.modulo.valor !== undefined && selectedTask.modulo.valor !== null && selectedTask.modulo.valor !== '') ? "✓" : "✗"} 
                      color={selectedTask.modulo && (selectedTask.modulo.valor !== undefined && selectedTask.modulo.valor !== null && selectedTask.modulo.valor !== '') ? "success" : "error"}
                      size="small"
                    />
                  }
                >
                  <ListItemText
                    primary="Módulo"
                    secondary="La tarea debe tener un módulo especificado"
                  />
                </ListItem>
                
                <ListItem
                  secondaryAction={
                    <Chip 
                      label={taskAttachments && taskAttachments.length > 0 ? "✓" : "✗"} 
                      color={taskAttachments && taskAttachments.length > 0 ? "success" : "error"}
                      size="small"
                    />
                  }
                >
                  <ListItemText
                    primary="Documentos Adjuntos"
                    secondary="La tarea debe tener al menos un documento adjunto"
                  />
                </ListItem>
              </List>
              
              <Alert severity="info" sx={{ mt: 2 }}>
                Todos estos campos deben ser completados en ClickUp antes de poder autorizar la tarea.
              </Alert>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleCloseRequirements} 
            variant="contained" 
            color="primary"
          >
            Entendido
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo de resultado de integración con Kepler */}
      <Dialog
        open={keplerResultDialog}
        onClose={handleCloseKeplerResult}
        maxWidth="md"
        fullWidth
        aria-labelledby="kepler-result-dialog"
      >
        <DialogTitle sx={{ bgcolor: 'success.light', color: 'success.contrastText', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckCircleIcon />
            <Typography variant="h6">
              Tarea Autorizada Exitosamente
            </Typography>
          </Box>
          <IconButton edge="end" color="inherit" onClick={handleCloseKeplerResult} aria-label="close">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 3 }}>
          {keplerResult && (
            <>
              <Alert severity="success" sx={{ mb: 3 }}>
                La tarea ha sido autorizada y se ha cambiado su estado a "Pendiente" en ClickUp.
              </Alert>
              
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                Integración con Kepler
              </Typography>
              
              <Paper elevation={2} sx={{ p: 2, mb: 3, bgcolor: keplerResult.registered ? 'success.50' : 'error.50' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  {keplerResult.registered ? (
                    <CheckCircleIcon color="success" sx={{ mr: 1 }} />
                  ) : (
                    <ErrorIcon color="error" sx={{ mr: 1 }} />
                  )}
                  <Typography variant="subtitle1" fontWeight="bold">
                    {keplerResult.registered 
                      ? 'Registro en Kepler completado' 
                      : 'Error al registrar en Kepler'}
                  </Typography>
                </Box>
                
                {keplerResult.error && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {keplerResult.error}
                  </Alert>
                )}
                
                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                  Se han enviado los siguientes datos al sistema Kepler ERP:
                </Typography>
                
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.paper' }}>
                      <Typography variant="subtitle2" color="primary" gutterBottom>
                        Hash de Orden (c78)
                      </Typography>
                      <Typography variant="body2" sx={{ wordBreak: 'break-all', fontFamily: 'monospace' }}>
                        {keplerResult.hashOrden}
                      </Typography>
                      {keplerResult.ordenString && (
                        <>
                          <Typography variant="subtitle2" color="primary" sx={{ mt: 1 }} gutterBottom>
                            Cadena Original (incluye ID de orden en mayúsculas):
                          </Typography>
                          <Typography variant="body2" sx={{ wordBreak: 'break-all', fontFamily: 'monospace' }}>
                            {keplerResult.ordenString}
                          </Typography>
                        </>
                      )}
                    </Paper>
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.paper' }}>
                      <Typography variant="subtitle2" color="primary" gutterBottom>
                        Hash de Adjuntos PDF (c79)
                      </Typography>
                      <Typography variant="body2" sx={{ wordBreak: 'break-all', fontFamily: 'monospace' }}>
                        {keplerResult.hashPDF}
                      </Typography>
                    </Paper>
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.paper' }}>
                      <Typography variant="subtitle2" color="primary" gutterBottom>
                        Hash de Integración (c80)
                      </Typography>
                      <Typography variant="body2" sx={{ wordBreak: 'break-all', fontFamily: 'monospace' }}>
                        {keplerResult.hashIntegracion}
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>
              </Paper>
              
              {/* Nueva sección para mostrar información del documento generado */}
              {keplerResult.document && (
                <Paper elevation={2} sx={{ p: 2, mb: 3, bgcolor: keplerResult.document.generated ? 'success.50' : 'error.50' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    {keplerResult.document.generated ? (
                      <CheckCircleIcon color="success" sx={{ mr: 1 }} />
                    ) : (
                      <ErrorIcon color="error" sx={{ mr: 1 }} />
                    )}
                    <Typography variant="subtitle1" fontWeight="bold">
                      {keplerResult.document.generated 
                        ? 'Documento generado en Kepler' 
                        : 'Error al generar documento en Kepler'}
                    </Typography>
                  </Box>
                  
                  {keplerResult.document.error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      {keplerResult.document.error}
                    </Alert>
                  )}
                  
                  {keplerResult.document.generated && (
                    <>
                      <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                        Se ha generado el siguiente documento en el sistema Kepler ERP:
                      </Typography>
                      
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.paper' }}>
                            <Typography variant="subtitle2" color="primary" gutterBottom>
                              Documento en Kepler
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <DescriptionIcon color="primary" />
                              <Typography variant="body1">
                                Folio: <strong>{keplerResult.document.folio}</strong>
                              </Typography>
                            </Box>
                            <Typography variant="body2" sx={{ mt: 1 }}>
                              Documento tipo <strong>U-D-69-{keplerResult.document.folio.includes('1') ? '1' : keplerResult.document.folio.includes('2') ? '2' : '3'}</strong> generado con éxito.
                            </Typography>
                          </Paper>
                        </Grid>
                      </Grid>
                    </>
                  )}
                </Paper>
              )}
              
              <Alert severity={keplerResult.registered ? "info" : "warning"} sx={{ mt: 2 }}>
                {keplerResult.registered 
                  ? "Estos hashes garantizan la integridad de la información entre ClickUp y Kepler, y pueden ser utilizados para auditoría futura."
                  : "La tarea se autorizó en ClickUp pero no se pudo registrar en Kepler. Los hashes generados se han guardado para intentar registrarlos más tarde."}
              </Alert>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleCloseKeplerResult} 
            variant="contained" 
            color="primary"
            startIcon={<CheckCircleOutlineIcon />}
          >
            Aceptar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo de subtareas */}
      <Dialog
        open={openSubtasksDialog}
        onClose={handleCloseSubtasksDialog}
        maxWidth="md"
        fullWidth
        aria-labelledby="subtasks-dialog"
      >
        <DialogTitle sx={{ 
          bgcolor: 'primary.light',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center' 
        }}>
          <Box>
            <Typography variant="h6">
              Subtareas
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Tarea principal: {selectedTask?.name}
            </Typography>
          </Box>
          <IconButton edge="end" color="inherit" onClick={handleCloseSubtasksDialog} aria-label="close">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 2 }}>
          {loadingSubtasks ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : subtasksError ? (
            <Alert severity="error" sx={{ mb: 2 }}>{subtasksError}</Alert>
          ) : taskSubtasks.length === 0 ? (
            <Alert severity="info">Esta tarea no tiene subtareas.</Alert>
          ) : (
            <List>
              {taskSubtasks.map((subtask) => (
                <ListItem
                  key={subtask.id}
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    mb: 1,
                    '&:hover': {
                      bgcolor: 'action.hover'
                    }
                  }}
                >
                  <ListItemIcon>
                    <Chip
                      label={subtask.status}
                      color={getStatusColor(subtask.status)}
                      size="small"
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={subtask.name}
                    secondary={
                      <React.Fragment>
                        <Typography variant="body2" component="span" color="text.secondary">
                          ID: {subtask.id}
                        </Typography>
                        {subtask.dateCreated && (
                          <Typography variant="body2" component="span" color="text.secondary" sx={{ ml: 2 }}>
                            Creada: {formatDate(subtask.dateCreated)}
                          </Typography>
                        )}
                        {subtask.dueDate && (
                          <Typography variant="body2" component="span" color="error.main" sx={{ ml: 2, fontWeight: 'medium' }}>
                            Fecha Fin: {formatDate(subtask.dueDate)}
                          </Typography>
                        )}
                        {subtask.modulo && (
                          <Typography variant="body2" component="span" color="info.main" sx={{ ml: 2, fontWeight: 'medium' }}>
                            Módulo: {subtask.modulo.descripcion || (subtask.modulo.valor !== undefined && subtask.modulo.valor !== null ? subtask.modulo.valor : 'N/A')}
                          </Typography>
                        )}
                        {subtask.assignees && subtask.assignees.length > 0 && (
                          <Typography variant="body2" component="span" color="primary.main" sx={{ ml: 2, fontWeight: 'medium' }}>
                            Asignada a: {subtask.assignees.join(', ')}
                          </Typography>
                        )}
                        {subtask.montoConsumido && (
                          <Typography variant="body2" component="span" color="warning.main" sx={{ ml: 2, fontWeight: 'medium' }}>
                            Monto Consumido: ${parseFloat(subtask.montoConsumido.valor).toLocaleString('es-MX')}
                          </Typography>
                        )}
                      </React.Fragment>
                    }
                  />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSubtasksDialog} color="primary">
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo de confirmación de recepción */}
      <Dialog
        open={confirmReceptionDialogOpen}
        onClose={cancelReceptionConfirmation}
        maxWidth="md"
        fullWidth
        aria-labelledby="confirm-reception-dialog"
      >
        <DialogTitle sx={{ bgcolor: 'primary.light', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" component="div" fontWeight="bold">
            Confirmar Recepción de Tarea
          </Typography>
          <IconButton edge="end" color="inherit" onClick={cancelReceptionConfirmation} aria-label="close">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 3 }}>
          {selectedTask && (
            <>
              <Alert severity="info" sx={{ mb: 3 }}>
                Al confirmar la recepción de esta tarea, se registrará la fecha y hora actual y su usuario en el sistema.
              </Alert>
              
              <Typography variant="h6" gutterBottom>
                {selectedTask.name}
              </Typography>
              
              <Paper elevation={1} sx={{ p: 2, mb: 2, bgcolor: 'background.paper' }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="textSecondary">
                      ID de Tarea
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      {selectedTask.id}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Estado Actual
                    </Typography>
                    <Chip 
                      label={selectedTask.status} 
                      color="primary"
                      size="small" 
                      sx={{ fontWeight: 'medium' }}
                    />
                  </Grid>
                </Grid>
              </Paper>
              
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="body1" gutterBottom>
                  <strong>Importante:</strong> Al confirmar la recepción:
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon>
                      <DateRangeIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Se registrará la fecha de recepción" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <EmailIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Se registrará su correo electrónico" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <LockIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Se generará un hash de recepción para auditoría" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <NotificationsIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Recibirá un correo de confirmación" />
                  </ListItem>
                </List>
              </Alert>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelReceptionConfirmation} variant="outlined" color="inherit">
            Cancelar
          </Button>
          <Button 
            onClick={proceedWithReceptionConfirmation} 
            variant="contained" 
            color="primary"
            disabled={isConfirmingReception}
            sx={{
              fontWeight: 'bold',
              boxShadow: 2
            }}
          >
            {isConfirmingReception ? (
              <>
                <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                Procesando...
              </>
            ) : (
              'Confirmar Recepción'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialogo de confirmacion para Aprobar solicitud */}
      <Dialog
        open={approveDialogOpen}
        onClose={handleCloseApprovalDialogs}
        maxWidth="sm"
        fullWidth
        aria-labelledby="approve-request-dialog"
      >
        <DialogTitle id="approve-request-dialog" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ThumbUpAltOutlinedIcon color="success" />
          Aprobar solicitud
        </DialogTitle>
        <DialogContent dividers>
          {selectedTask && (
            <DialogContentText component="div">
              <Typography variant="body1" gutterBottom>
                Esta accion cambiara la tarea al estado <strong>Aprobado</strong> en ClickUp.
              </Typography>
              <Box sx={{ mt: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Tarea
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {selectedTask.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  ID: {selectedTask.id}
                </Typography>
              </Box>

              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2" gutterBottom>
                  La aprobación de este requerimiento autoriza a los asesores de Kepler a revisarlo y canalizarlo según corresponda:
                </Typography>
                <Box component="ul" sx={{ pl: 2.5, mb: 0, mt: 1 }}>
                  <li>
                    <Typography variant="body2">
                      Se integrará a su <strong>póliza de soporte</strong>, si cuenta con una vigente.
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body2">
                      Se incorporará a una <strong>orden abierta</strong> existente.
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body2">
                      O en su caso, se aplicará a una <strong>garantía vigente</strong>.
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body2">
                      Si requiere cotización, se generará una <strong>orden cerrada</strong> que se enviará para su autorización posterior.
                    </Typography>
                  </li>
                </Box>
              </Alert>

              {approvalError && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {approvalError}
                </Alert>
              )}
            </DialogContentText>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseApprovalDialogs}
            variant="outlined"
            color="inherit"
            disabled={isProcessingApproval}
          >
            Cancelar
          </Button>
          <Button
            onClick={proceedWithApprove}
            variant="contained"
            color="success"
            disabled={isProcessingApproval}
            startIcon={isProcessingApproval ? <CircularProgress size={18} color="inherit" /> : <CheckCircleIcon />}
          >
            {isProcessingApproval ? 'Aprobando...' : 'Confirmar aprobación'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialogo de Denegar solicitud (motivo obligatorio) */}
      <Dialog
        open={denyDialogOpen}
        onClose={handleCloseApprovalDialogs}
        maxWidth="sm"
        fullWidth
        aria-labelledby="deny-request-dialog"
      >
        <DialogTitle id="deny-request-dialog" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ThumbDownAltOutlinedIcon color="error" />
          Denegar solicitud
        </DialogTitle>
        <DialogContent dividers>
          {selectedTask && (
            <>
              <Typography variant="body1" gutterBottom>
                Esta accion cambiara la tarea al estado <strong>Denegado</strong> y registrara el motivo en ClickUp.
              </Typography>
              <Box sx={{ mt: 2, mb: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Tarea
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {selectedTask.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  ID: {selectedTask.id}
                </Typography>
              </Box>
              <TextField
                label="Motivo de rechazo"
                placeholder="Describa por que se deniega esta solicitud..."
                value={denyReason}
                onChange={(e) => setDenyReason(e.target.value)}
                multiline
                minRows={3}
                fullWidth
                required
                disabled={isProcessingApproval}
                error={!denyReason.trim() && !!approvalError}
                helperText={denyReason.trim().length === 0 ? 'El motivo es obligatorio.' : `${denyReason.trim().length} caracteres`}
              />
              {approvalError && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {approvalError}
                </Alert>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseApprovalDialogs}
            variant="outlined"
            color="inherit"
            disabled={isProcessingApproval}
          >
            Cancelar
          </Button>
          <Button
            onClick={proceedWithDeny}
            variant="contained"
            color="error"
            disabled={isProcessingApproval || !denyReason.trim()}
            startIcon={isProcessingApproval ? <CircularProgress size={18} color="inherit" /> : <ThumbDownAltOutlinedIcon />}
          >
            {isProcessingApproval ? 'Denegando...' : 'Confirmar denegacion'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar de exito tras aprobar/denegar */}
      <Snackbar
        open={approvalSnackbar.open}
        autoHideDuration={5000}
        onClose={() => setApprovalSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={approvalSnackbar.severity}
          variant="filled"
          onClose={() => setApprovalSnackbar(prev => ({ ...prev, open: false }))}
          sx={{ width: '100%' }}
        >
          {approvalSnackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CategoryTasksPage; 